import React, { MutableRefObject, useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AudioPlayerHandle } from "../components/AudioPlayer";
import { JourneyStrip } from "./meeting/JourneyStrip";
import {
  ChecklistItem, IN_FLIGHT_STATUSES, SPEAKER_SUGGESTION_KINDS, MISHEARING_KINDS,
  deriveJourney, readSummaryRead, writeSummaryRead,
} from "../lib/journey";
import { ProposedAttendee } from "../components/ProposedAttendeesCard";
import { ProposedTerm } from "../components/ProposedGlossaryCard";
import { MeetingSynopsis } from "../components/MeetingSynopsis";
import { ReviewPanelHandle } from "../components/ReviewPanel";
import { SpeakerOverride } from "../components/SpeakerOverride";
import { Toast } from "../components/Toast";
import { BackToTop } from "../components/BackToTop";
import { Tabs } from "../ui/Tabs";
import { Avatar } from "../ui/Avatar";
import { Icon } from "../ui/icons";
import { Skeleton } from "../ui/Skeleton";
import { MeetingHeader, canReAnnotate } from "./meeting/MeetingHeader";
import { ReviewRail, readRailCollapsed, writeRailCollapsed } from "./meeting/ReviewRail";
import { PlayerDock } from "./meeting/PlayerDock";
import { SummaryTab } from "./meeting/SummaryTab";
import { TranscriptTab } from "./meeting/TranscriptTab";
import { t, tVars } from "../i18n";
import { ReworkDialog } from "./meeting/ReworkDialog";
import { DangerZone } from "../components/DangerZone";
import { Modal } from "../ui/Modal";
import {
  assignMeetingToProject,
  getPlaybackUrl,
  lockMeeting,
  reAnnotateMeeting,
  regenerateSummary,
  resubmitMeeting,
  reworkSection,
  submitContextForSoniox,
  unlockMeeting,
} from "../lib/api";
import { isRecoverableOrphan } from "../lib/orphanRecovery";
import { loadActiveProjects, ProjectOption } from "../lib/projects";
import { promotionToastText } from "../lib/promotionToast";
import { loadBothIgnores, filterIgnored } from "../lib/registryIgnores";
import {
  Correction,
  SpanAttribution,
  appendSpanAttribution,
  isTurnUserModified,
  removeSpanAttribution,
  renameSpanAttribution,
  resolveCorrectionForToken,
  resolveTurnSpeaker,
  saveWysiwygEditSession,
  setCorrectionStatus,
} from "../lib/corrections";
import {
  BookmarkClearPayload,
  BookmarkSetPayload,
  toggleBookmarkArray,
  EditSessionPayload,
  MeetingEvent,
  MeetingEventPayload,
  ParagraphRevertPayload,
  hasMeetingEventSince,
  loadEventsByIds,
  loadLastUserEvent,
  logUserEvent,
} from "../lib/events";
import {
  ParagraphSplit,
  appendParagraphSplit,
  removeParagraphSplit,
} from "../lib/paragraphSplits";
import { Flag, hasPendingFlagForTurn } from "../lib/flags";
import { DriftFlag, aggregateReviewItems } from "../lib/reviewItems";
import { supabase } from "../lib/supabase";
import { tokenDir, turnDir, documentDir } from "../lib/script";
import { resolveSeekMs } from "../lib/seek";
import { wordSync } from "../lib/wordSync";
import { followReducer, INITIAL_FOLLOW_STATE, isWithinProgrammaticWindow } from "../lib/followState";
import { formatWallClock } from "../lib/timestamps";
import { assembleResolvedBefore, buildWholeSubTurnCorrection } from "../lib/wysiwyg";
import { v4 as uuidv4 } from "uuid";

interface SonioxContext {
  general?: { key: string; value: string }[];
  terms?: string[];
  text?: string;
}

interface Meeting {
  id: string;
  title: string | null;
  status: string;
  prose: string;
  audio_duration_ms: number | null;
  error_message: string | null;
  r2_object_key: string | null;
  context: SonioxContext | null;
  recorded_at: string | null;
  title_recorded_at: string | null;
  proposed_attendees: ProposedAttendee[];
  proposed_glossary: ProposedTerm[];
  workspace_id: string;
  project_id: string | null;
  locked_at: string | null;
  diarization_flags: DriftFlag[];
  // Migration 0022: multi-bookmark per meeting. Each element is an audio
  // position ms anchored to a paragraph's start_ms. Empty array = no
  // bookmarks. Stored as ms (not turn index) so bookmarks survive paragraph
  // restructuring. NOT NULL DEFAULT '{}' in the DB.
  bookmarks_at_ms: number[];
}

interface SonioxToken {
  text: string;
  start_ms: number;
  end_ms: number;
  confidence?: number;
  speaker?: string;
  language?: string;
}

interface TranscriptPayload {
  meta: { id: string; model: string; audio_duration_ms?: number };
  transcript: { tokens?: SonioxToken[] };
}

// Migration 0032: structured meeting summary from the summary annotation pass.
export interface SummaryItem {
  text: string;
  start_ms: number;
  owner?: string; // action items only, when explicitly stated
}

export interface TranscriptSummary {
  overview: string;
  key_points: SummaryItem[];
  action_items: SummaryItem[];
  decisions: SummaryItem[];
  language: "he" | "en";
}

// Migration 0007 (2026-05-13): nested speaker_map shape.
// name may be null when Claude couldn't confidently assign.
interface SpeakerMapEntry {
  name: string | null;
  confidence: number;
  raw_label: string;
}

interface TranscriptRow {
  payload: TranscriptPayload;
  // speaker_map is ONLY written by the "Whole meeting" rename in the
  // SpeakerOverride popover (manual tier-2 override).  The retired
  // speaker_mapper.py no longer writes this field.
  speaker_map: Record<string, SpeakerMapEntry>;
  // Migration 0011 (2026-05-13): span-level speaker overrides.
  // Render cascade (5-tier) — see resolveTurnSpeaker in lib/corrections.ts:
  //   1. manual per-turn span  (span_attributions, source='manual')
  //   2. manual whole-meeting  (speaker_map[label])
  //   3. auto per-turn span    (span_attributions, source='auto'/'L3')
  //   4. auto label default    (attribution_label_map[label])
  //   5. raw label / "Not attributed"
  span_attributions: SpanAttribution[];
  // Migration 0015 (2026-05-20): intra-turn paragraph splits.
  // Each entry marks a token boundary where a new paragraph begins.
  paragraph_splits: ParagraphSplit[];
  // Annotation pipeline redesign (2026-05-25): 2-4 sentence overview
  // of the attribution logic produced by the attribution Claude call.
  // Null when the transcript predates the new pipeline.
  attribution_synopsis: string | null;
  // Migration 0025 (2026-05-30): speaker-level attribution defaults
  // (Claude's label-level output, tier 4 in the cascade above).
  // Empty object {} for meetings annotated before migration 0025.
  attribution_label_map: Record<string, { name: string | null; confidence: number }>;
  // Migration 0032: Claude-generated meeting summary. Null until the summary
  // annotation pass has run (or on transcripts from before Task 6 shipped).
  summary: TranscriptSummary | null;
  // Migration 0033 (M2): meeting-level transcription-integrity judgment from
  // the analysis. Null until the critique pass has run post-0033.
  // Migration 0033 is live; loadTranscript's select list now includes `integrity`.
  integrity: { verdict: "ok" | "suspect" | "broken"; reason: string } | null;
}

// Graduated confidence thresholds for speaker-label rendering
// (2026-05-25 redesign). Storage no longer gates on these; only
// render-time decoration uses them.
//   ≥ CONFIDENCE_HIGH → silent (no marker)
//   CONFIDENCE_LOW-CONFIDENCE_HIGH → "?" glyph (medium uncertainty)
//   < CONFIDENCE_LOW  → low-confidence pill + italic + review-list item
const CONFIDENCE_HIGH = 0.85;
const CONFIDENCE_LOW = 0.6;

export function MeetingReview() {
  const { id } = useParams<{ id: string }>();
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [transcript, setTranscript] = useState<TranscriptRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reannotateDialogOpen, setReannotateDialogOpen] = useState(false);
  const [reannotating, setReannotating] = useState(false);
  const [extraContext, setExtraContext] = useState("");
  const [locking, setLocking] = useState(false);
  const [regenFiredAt, setRegenFiredAt] = useState<string | null>(null);
  const [reworkSelecting, setReworkSelecting] = useState(false);
  const [reworkRange, setReworkRange] = useState<{ startMs: number; endMs: number; paragraphs: number } | null>(null);
  const [reworkBusy, setReworkBusy] = useState(false);
  const [reworking, setReworking] = useState<{ startMs: number; endMs: number; firedAt: string } | null>(null);
  // Task 15 (M2): Start-over modal triggered by the broken-verdict chip.
  const [startOverOpen, setStartOverOpen] = useState(false);
  // Studio layout: active tab (Summary | Transcript) + rail collapse.
  const [activeTab, setActiveTab] = useState<"summary" | "transcript">("transcript");
  // Guard: auto-switch to Summary tab fires at most once per meeting id.
  // Realtime refetches must NOT teleport the user back to Summary mid-listening.
  const summaryAutoSwitchedRef = useRef<string | null>(null);
  const [railCollapsed, setRailCollapsed] = useState(() => readRailCollapsed());
  // Below 1280px the rail is a slide-over toggled by the header's "Review"
  // button. Desktop layout ignores this flag (CSS owns the breakpoint).
  const [mobileRailOpen, setMobileRailOpen] = useState(false);
  // "Hide my edits" / "Show my edits" toggle. When ON, the transcript
  // renders the raw Soniox tokens with NO overlays: no corrections,
  // no span attributions, no paragraph splits, original Soniox speaker
  // labels. Toggled via the sticky-header button OR by holding Alt
  // (peek). On Mac, e.altKey is the Option key — same semantics.
  // peekActive is the transient Alt-held state.
  //
  // Effective view-source is `sourceToggle XOR peekActive`: peek flips
  // the static toggle in either direction. Without XOR (e.g. plain OR),
  // peek can only force source-view ON, never temporarily back to the
  // edited view when sourceToggle is already ON — which broke the round-
  // trip the user expected: click "Hide my edits", then hold Alt to peek
  // your edits back, then release.
  const [sourceToggle, setSourceToggle] = useState(false);
  const [peekActive, setPeekActive] = useState(false);
  const viewingSource = sourceToggle !== peekActive;

  // Alt-to-peek listeners. Document-level so they fire regardless of
  // focus, EXCEPT when the user is typing in an input — there, Alt may
  // be used as a modifier (e.g., Mac Option+letter for accents) and
  // shouldn't trigger a peek.
  useEffect(() => {
    function shouldIgnoreAlt(): boolean {
      const active = document.activeElement;
      return (
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        (active instanceof HTMLElement && active.isContentEditable)
      );
    }
    function handleDown(e: KeyboardEvent) {
      if (e.key !== "Alt") return;
      if (shouldIgnoreAlt()) return;
      if (e.repeat) return;
      setPeekActive(true);
    }
    function handleUp(e: KeyboardEvent) {
      if (e.key !== "Alt") return;
      setPeekActive(false);
    }
    function handleBlur() {
      // Releasing Alt outside the window (e.g., switching tabs while
      // holding Alt+Tab) doesn't fire keyup. Reset peek on blur.
      setPeekActive(false);
    }
    document.addEventListener("keydown", handleDown);
    document.addEventListener("keyup", handleUp);
    window.addEventListener("blur", handleBlur);
    return () => {
      document.removeEventListener("keydown", handleDown);
      document.removeEventListener("keyup", handleUp);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  // Scroll-detection for the follow toggle. Listens on main.page (the real
  // scroll container). When the user scrolls manually, disengages following.
  // Programmatic scrolls (smooth scrollIntoView from the rAF loop, click-to-
  // seek recenter, Follow re-engage) are guarded by a TIMESTAMP WINDOW:
  // smooth scrolling fires a stream of scroll events, so every event within
  // PROGRAMMATIC_SCROLL_WINDOW_MS of the last programmatic mark is ignored.
  useEffect(() => {
    const container = document.querySelector("main.page");
    if (!container) return;
    function handleScroll() {
      if (isWithinProgrammaticWindow(programmaticScrollRef.current, Date.now())) {
        return; // burst from our own scrollIntoView — not the user
      }
      // The user scrolled on their own: they want to read elsewhere.
      // Disengage following (no-op if already disengaged).
      dispatchFollow("manual_scroll");
    }
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  // Hygiene fix (2026-05-14, Stream 3): the previous shape silently
  // swallowed every error from /api/playback-url via `.catch(() => {})`,
  // which made the AudioPlayer card disappear on transient backend
  // errors (the wedged-httpx-pool incident: meeting open, restart
  // backend, AudioPlayer never reappears because the SPA quietly
  // dropped the error). Now we capture the error + offer a Retry
  // button instead of disappearing the card.
  const [playbackError, setPlaybackError] = useState<string | null>(null);
  const [playbackAttempt, setPlaybackAttempt] = useState(0);
  const playerRef = useRef<AudioPlayerHandle | null>(null);
  const reviewPanelRef = useRef<ReviewPanelHandle | null>(null);
  const reannotateDialogRef = useRef<HTMLDialogElement | null>(null);

  // Dynamic sticky-header height. The title can wrap to multiple lines (a long
  // title, or while renaming it), so the header is no longer a fixed 64px.
  // Measure it and publish --sticky-header-h so the AudioPlayer below (which
  // sticks at top: var(--sticky-header-h)) follows instead of being overlapped.
  // Reset to the CSS default (global.css) on unmount so other pages are
  // unaffected.
  const headerResizeObsRef = useRef<ResizeObserver | null>(null);
  const stickyHeaderRef = useCallback((node: HTMLDivElement | null) => {
    headerResizeObsRef.current?.disconnect();
    headerResizeObsRef.current = null;
    if (!node) {
      document.documentElement.style.removeProperty("--sticky-header-h");
      return;
    }
    if (typeof ResizeObserver === "undefined") return;
    const apply = () =>
      document.documentElement.style.setProperty(
        "--sticky-header-h",
        `${node.offsetHeight}px`,
      );
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(node);
    headerResizeObsRef.current = ro;
  }, []);

  // Wide-page class: the meeting studio layout uses a rail + transcript side
  // by side and needs the full available width so the rail scrollbar lives at
  // the right edge of the layout. Add page--wide on mount (removes max-width);
  // remove on unmount so other pages are unaffected.
  useEffect(() => {
    const el = document.querySelector("main.page");
    if (!el) return;
    el.classList.add("page--wide");
    return () => el.classList.remove("page--wide");
  }, []);

  // Selecting-page class: while the rework selection dock is mounted it stacks
  // a second bar above the player. On mobile (where both docks are fixed) the
  // page needs extra bottom padding so the last paragraph clears BOTH bars.
  // .page--selecting toggles that padding (see global.css mobile rule).
  useEffect(() => {
    const el = document.querySelector("main.page");
    if (!el) return;
    if (reworkSelecting) {
      el.classList.add("page--selecting");
      return () => el.classList.remove("page--selecting");
    }
    el.classList.remove("page--selecting");
  }, [reworkSelecting]);

  // Playback time fan-out from AudioPlayer. Ref, not state, so the
  // ~20x/sec audioprocess tick doesn't re-render MeetingReview or the
  // transcript card. TranscriptView reads this through a single rAF
  // loop and updates its own activeTurnIdx state only on change.
  const playbackTimeRef = useRef(0);
  // Follow toggle: when true, the active paragraph auto-scrolls into view.
  // When the user manually scrolls the transcript during playback, this
  // disengages. The PlayerDock Follow button re-engages it.
  const [followState, dispatchFollow] = useReducer(followReducer, INITIAL_FOLLOW_STATE);
  // Timestamp guard: programmatic scrollIntoView callers (rAF loop /
  // editRequest effect / click-to-seek recenter / Follow re-engage) write
  // Date.now() here before calling scrollIntoView. The scroll listener
  // ignores every event inside the window (smooth scrolling fires a STREAM
  // of scroll events — a one-shot boolean only swallowed the first and the
  // rest falsely disengaged Following; see isWithinProgrammaticWindow).
  const programmaticScrollRef = useRef(0);
  // Pending seek: set by SummaryTab timestamp links when the user switches
  // to the Transcript tab (which may not be mounted yet). An effect fires
  // it once the transcript tab is active, then clears it. Cleared on unmount.
  const pendingSeekRef = useRef<number | null>(null);
  // References drawer state removed — content now in ReviewRail (Task 5 refactor).
  const [lastUserEvent, setLastUserEvent] = useState<MeetingEvent | null>(null);
  const [undoing, setUndoing] = useState(false);
  // Sentinel flag rows for this meeting. Loaded via supabase-js +
  // refreshed on every Realtime change to the `flags` table. See
  // DECISIONS.md 2026-05-13 "Flag panel UI" entry.
  const [flags, setFlags] = useState<Flag[]>([]);
  // Task 5: text correction rows for this meeting. Loaded + refreshed
  // on every Realtime change to the `corrections` table. status
  // filtered at render time (accepted overlays; dismissed inert).
  const [corrections, setCorrections] = useState<Correction[]>([]);
  // Annotation pipeline redesign (2026-05-25): synopsis from the
  // attribution Claude call. Null when the meeting was annotated before
  // the new pipeline shipped, or when re-annotation hasn't run yet.
  const [attributionSynopsis, setAttributionSynopsis] = useState<string | null>(null);
  // Item 2: cross-component request to open the inline SpeakerOverride
  // popover at the turn covering a given ms. Set when the user clicks
  // Edit on a Claude speaker proposal in the ReviewPanel; consumed by
  // TranscriptView's useEffect (ms -> turn idx -> setEditingTurnIdx).
  // Nonce-keyed so repeat clicks on the same row re-fire the effect
  // even when ms is unchanged.
  const [editRequest, setEditRequest] = useState<{ ms: number; nonce: number } | null>(null);

  // Editable copies of the meeting's proposed attendees + glossary, owned
  // here so the user can fix a spelling or drop a wrong entry on the
  // proposed cards BEFORE assigning — curate → clean import (spec §4).
  // Seeded from meeting.* whenever the meeting loads/changes; the edit/
  // remove handlers persist the updated array back to the meetings row
  // and update this local state. Passed (with the handlers + meetingId)
  // to the inline cards AND the references drawer so both stay in sync.
  const [proposedAttendees, setProposedAttendees] = useState<ProposedAttendee[]>([]);
  const [proposedGlossary, setProposedGlossary] = useState<ProposedTerm[]>([]);

  // Ignore sets: values the user previously dismissed from the project's
  // glossary / attendee registry. Built from registry_ignores rows loaded
  // when the meeting's project_id is known. Display-only — never written
  // back to the DB. Re-loaded after refreshAll so a freshly-ignored value
  // disappears from the proposed cards immediately.
  const [ignoredTermSet, setIgnoredTermSet] = useState<Set<string>>(new Set());
  const [ignoredNameSet, setIgnoredNameSet] = useState<Set<string>>(new Set());

  // Stable ref so loadProjectIgnores can read the current project_id
  // without closing over the meeting state (which would make it
  // unstable and cause render loops via refreshAll deps).
  const projectIdRef = useRef<string | null>(null);
  projectIdRef.current = meeting?.project_id ?? null;

  // loadProjectIgnores: fetch both glossary + attendee ignore rows for the
  // current project and store as Sets. Empty deps → stable callback; reads
  // project_id from the ref so refreshAll can safely include it without a
  // loop. Clears both sets when no project is assigned.
  const loadProjectIgnores = useCallback(async () => {
    const pid = projectIdRef.current;
    if (!pid) {
      setIgnoredTermSet(new Set());
      setIgnoredNameSet(new Set());
      return;
    }
    try {
      const { glossary: termRows, attendee: attendeeRows } = await loadBothIgnores(pid);
      setIgnoredTermSet(new Set(termRows.map((r) => r.value_normalized)));
      setIgnoredNameSet(new Set(attendeeRows.map((r) => r.value_normalized)));
    } catch {
      // Best-effort: display filter degrades gracefully to no filtering
      // on error rather than surfacing a noisy error to the user.
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-run when project_id changes (including null → assignment → assigned).
  // loadProjectIgnores is stable (empty deps) so this effect does not loop.
  useEffect(() => {
    void loadProjectIgnores();
  }, [meeting?.project_id, loadProjectIgnores]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Task 3 (2026-06-01): project assign control on the meeting screen.
  // Loaded once on mount; used to populate the project dropdown.
  const [availableProjects, setAvailableProjects] = useState<ProjectOption[]>([]);
  // Controls whether the dropdown (or new-project inline input) is open.
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  // "new" mode: user typed "+ New project…", we show an inline text input.
  const [newProjectName, setNewProjectName] = useState("");
  const [creatingProject, setCreatingProject] = useState(false);
  const [assigningProject, setAssigningProject] = useState(false);
  // True when the user picked "+ New project…" — shows the inline name input
  // independently of newProjectName (which starts empty). Decoupling these is
  // required: gating the input on `newProjectName !== ""` made the input vanish
  // the instant it was cleared, so you could never type a name.
  const [newProjectMode, setNewProjectMode] = useState(false);
  // Staged project choice while the picker is open. The dropdown updates ONLY
  // this; the assignment commits exclusively via the explicit "Assign" button,
  // so an accidental touch of the list never reassigns (which would re-promote
  // names/terms). Deliberate, positive action required to change.
  const [pendingProjectId, setPendingProjectId] = useState<string | null>(null);
  // The assigned project's display NAME (never the raw UUID). Resolved from the
  // active-projects list when possible, else fetched directly so an ARCHIVED
  // project (absent from loadActiveProjects) still shows a readable name + link.
  const [assignedProjectName, setAssignedProjectName] = useState<string | null>(null);
  // Post-assign promotion summary toast. Null = no toast.
  const [promotionToast, setPromotionToast] = useState<string | null>(null);
  // Dismiss/remove toast from the proposed cards (toast + optional undo).
  const [cardToast, setCardToast] = useState<{ message: string; undoFn?: () => void } | null>(null);

  // Pre-Soniox context review state (Group C, 2026-05-29).
  // Only active when meeting.status === 'awaiting_context_review'.
  // Fields are initialised from meeting.context when that status first arrives
  // (via the useEffect below that watches meeting.status).
  const [contextReviewText, setContextReviewText] = useState("");
  const [contextReviewTerms, setContextReviewTerms] = useState<string[]>([]);
  const [contextReviewTermInput, setContextReviewTermInput] = useState("");
  const [contextReviewGeneral, setContextReviewGeneral] = useState<{ key: string; value: string }[]>([]);
  const [submittingContext, setSubmittingContext] = useState(false);

  // Imperatively drive the native <dialog> element. Browser handles focus
  // trap + Esc-to-close; we still need to sync state when the user closes
  // via Esc or backdrop click (see onDialogClose).
  useEffect(() => {
    const dlg = reannotateDialogRef.current;
    if (!dlg) return;
    if (reannotateDialogOpen && !dlg.open) dlg.showModal();
    if (!reannotateDialogOpen && dlg.open) dlg.close();
  }, [reannotateDialogOpen]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setPlaybackError(null);
    getPlaybackUrl(id)
      .then((r) => {
        if (!cancelled) {
          setPlaybackUrl(r.url);
          setPlaybackError(null);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        // Distinguish "audio not ready yet" (409 from the backend
        // before Soniox has produced verbatim) from real errors
        // (httpx pool wedged, network failure, 5xx). The 409 case
        // is expected for new meetings and shouldn't surface a
        // scary error -- it'll just appear once status reaches
        // 'ready'. Anything else surfaces a retry affordance.
        const message = e instanceof Error ? e.message : String(e);
        const isNotReady = message.includes("409");
        if (isNotReady) {
          // Quiet path: meeting isn't ready yet. AudioPlayer card
          // simply doesn't render until status flips.
          setPlaybackUrl(null);
          setPlaybackError(null);
        } else {
          setPlaybackUrl(null);
          setPlaybackError(message || "Failed to load audio");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id, playbackAttempt]);

  function seekTo(ms: number) {
    playerRef.current?.seekToMs(ms);
    playerRef.current?.play();
  }

  // Fire pending seek once the transcript tab is active and the player is ready.
  // Cleared on unmount to avoid seeks after navigation.
  useEffect(() => {
    if (activeTab !== "transcript") return;
    if (pendingSeekRef.current === null) return;
    const ms = pendingSeekRef.current;
    pendingSeekRef.current = null;
    seekTo(ms);
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => { pendingSeekRef.current = null; };
  }, []);

  // Load functions are useCallbacks at component scope (not closure-trapped
  // inside the mount useEffect) so they can be called from edit-mode action
  // handlers to force-refresh data without waiting for Realtime.
  //
  // 2026-05-29 user-verified architecture: action handlers explicitly refresh
  // after their DB write completes. Realtime delivery has proven unreliable
  // in practice (the original 2026-05-21 incident), and the user confirmed
  // empirically that without explicit refresh, saves don't render until a
  // manual page reload.
  //
  // The 60-90s freeze that triggered the perf fix earlier today came from
  // EXPENSIVE per-render cost, NOT from the double-trigger itself. With the
  // perf optimizations now in place (TurnRow React.memo + tokenDir cache +
  // stable RawTokens keys + stabilized tokens ref), a full refreshAll
  // re-renders cheaply (~50-200ms), so the action-write + explicit-refresh
  // double-trigger is safe AND necessary for reliable UI updates.
  const loadMeeting = useCallback(async () => {
    if (!id) return;
    const { data, error } = await supabase
      .from("meetings")
      .select("id, title, status, prose, audio_duration_ms, error_message, r2_object_key, context, recorded_at, title_recorded_at, proposed_attendees, proposed_glossary, workspace_id, project_id, locked_at, diarization_flags, bookmarks_at_ms")
      .eq("id", id)
      .maybeSingle();
    if (error) setError(error.message);
    else if (data) setMeeting(data as Meeting);
  }, [id]);

  const loadTranscript = useCallback(async () => {
    if (!id) return;
    const { data, error } = await supabase
      .from("transcripts")
      .select("payload, speaker_map, span_attributions, paragraph_splits, attribution_synopsis, attribution_label_map, summary, integrity")
      .eq("meeting_id", id)
      .maybeSingle();
    if (error) setError(error.message);
    else if (data) {
      const row = data as TranscriptRow;
      setTranscript(row);
      setAttributionSynopsis(row.attribution_synopsis ?? null);
      // Auto-switch to Summary tab when a summary is available — gated by a
      // ref so it fires AT MOST ONCE per meeting id. Realtime refetches must
      // not teleport the user back to Summary while they're on the Transcript tab.
      if (row.summary && id && summaryAutoSwitchedRef.current !== id) {
        summaryAutoSwitchedRef.current = id;
        setActiveTab((prev) => prev === "transcript" ? "summary" : prev);
      }
    } else {
      // No transcripts row (e.g. a never-started orphan, or after navigating
      // here from a meeting that HAD one). Clear stale state so the
      // recoverable-orphan predicate (transcript === null) reads true.
      setTranscript(null);
      setAttributionSynopsis(null);
    }
  }, [id]);

  const loadCorrections = useCallback(async () => {
    if (!id) return;
    const { data, error } = await supabase
      .from("corrections")
      .select(
        "id, meeting_id, workspace_id, flag_id, start_ms, end_ms, original_text, corrected_text, promote_to_glossary, status, created_at, pre_correction_status",
      )
      .eq("meeting_id", id)
      .order("created_at");
    if (error) setError(error.message);
    else setCorrections((data ?? []) as Correction[]);
  }, [id]);

  const loadFlags = useCallback(async () => {
    if (!id) return;
    const { data, error } = await supabase
      .from("flags")
      .select("id, meeting_id, kind, severity, start_ms, end_ms, reason, status, evidence, created_at")
      .eq("meeting_id", id)
      .order("kind")
      .order("start_ms");
    if (error) setError(error.message);
    else setFlags((data ?? []) as Flag[]);
  }, [id]);

  // refreshAll: full data reload (meeting + transcript + corrections + last
  // user event). Called from every action handler after its DB write succeeds
  // so the UI reflects the new state without waiting for Realtime. Safe to
  // double-trigger with Realtime delivery because the perf optimizations
  // (TurnRow React.memo + tokenDir cache + stable keys + stabilized tokens
  // ref) keep per-render cost low. See the comment above loadMeeting for the
  // 2026-05-29 user-verified architecture rationale.
  const refreshAll = useCallback(async () => {
    await Promise.all([
      loadMeeting(),
      loadTranscript(),
      loadCorrections(),
    ]);
    try {
      const ev = await loadLastUserEvent(id ?? "");
      setLastUserEvent(ev);
    } catch { /* best-effort */ }
    // Reload ignores so a value just added to registry_ignores disappears
    // from the proposed cards without a manual page reload. loadProjectIgnores
    // is stable (empty deps + ref-based) so adding it here does not widen
    // the refreshAll dep array in a way that causes loops.
    try { await loadProjectIgnores(); } catch { /* best-effort */ }
  }, [loadMeeting, loadTranscript, loadCorrections, id, loadProjectIgnores]);

  useEffect(() => {
    if (!id) return;

    loadMeeting();
    loadTranscript();
    loadFlags();
    loadCorrections();
    loadLastUserEvent(id).then(setLastUserEvent).catch(() => {});

    const channel = supabase
      .channel(`meeting-${id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "meetings", filter: `id=eq.${id}` },
        () => { void loadMeeting(); }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "transcripts", filter: `meeting_id=eq.${id}` },
        () => { void loadTranscript(); }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "flags", filter: `meeting_id=eq.${id}` },
        () => { void loadFlags(); }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "corrections", filter: `meeting_id=eq.${id}` },
        () => { void loadCorrections(); }
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "meeting_events", filter: `meeting_id=eq.${id}` },
        () => { loadLastUserEvent(id).then(setLastUserEvent).catch(() => {}); }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [id, loadMeeting, loadTranscript, loadFlags, loadCorrections]);

  // Polling fallback for in-flight pipeline phases.
  //
  // Realtime delivery is empirically unreliable in this codebase (see the
  // 2026-05-21 incident in STATUS.md). During in-flight phases there are no
  // user actions to call forceRefresh, so the page sits waiting for Realtime
  // that may take 30+ seconds or never arrive cleanly. This belt-and-suspenders
  // poll catches up within 5 seconds if Realtime lags.
  //
  // When Realtime delivers first (the usual case), polling sees the new status
  // on its next tick and the interval cleans itself up automatically via the
  // dep array on meeting?.status. No infinite-loop risk.
  //
  // Uses refreshAll (not just loadMeeting) so that when annotation finishes the
  // transcript + corrections + flags also load in the same tick — otherwise the
  // status banner clears but the page remains empty until a manual refresh.
  // refreshAll is a stable useCallback (deps: [loadMeeting, loadTranscript,
  // loadCorrections, id]) — safe in the dep array.
  useEffect(() => {
    if (!meeting) return;
    if (!IN_FLIGHT_STATUSES.has(meeting.status)) return;
    const interval = setInterval(() => { void refreshAll(); }, 5000);
    return () => clearInterval(interval);
  }, [meeting?.status, refreshAll]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize the context review form fields when the meeting first enters
  // 'awaiting_context_review' status. This runs whenever meeting.context
  // or meeting.status changes so it stays current if the user re-opens the
  // page after the status has already been set.
  useEffect(() => {
    if (!meeting || meeting.status !== "awaiting_context_review") return;
    const ctx = meeting.context ?? {};
    setContextReviewText(ctx.text ?? "");
    setContextReviewTerms(ctx.terms ?? []);
    setContextReviewGeneral(ctx.general ?? []);
  }, [meeting?.status, meeting?.context]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Seed the editable proposed-attendees / proposed-glossary copies from the
  // meeting whenever it (re)loads. Keyed on the meeting id so an explicit
  // edit/remove (which updates both the meetings row and these states) is not
  // clobbered by an unrelated re-render; only a genuine new load re-seeds.
  useEffect(() => {
    if (!meeting) return;
    setProposedAttendees(meeting.proposed_attendees ?? []);
    setProposedGlossary(meeting.proposed_glossary ?? []);
  }, [meeting?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Persist the edited proposed arrays back to the meetings row. Optimistic:
  // update local state first, then write; on error surface it in the page
  // error bar (no silent failure). RLS allows workspace members to update
  // their own meetings row (same trust as every other SPA write here).
  const persistProposedAttendees = useCallback(
    async (next: ProposedAttendee[]) => {
      if (!id) return;
      // Capture the pre-edit array via the functional updater so we can roll
      // back if the write fails — otherwise the optimistic local state stays
      // ahead of the DB permanently (no re-seed fires; same meeting id).
      let prev: ProposedAttendee[] = [];
      setProposedAttendees((p) => {
        prev = p;
        return next;
      });
      const { error: e } = await supabase
        .from("meetings")
        .update({ proposed_attendees: next })
        .eq("id", id);
      if (e) {
        setProposedAttendees(prev);
        setError(e.message);
      } else setMeeting((m) => (m ? { ...m, proposed_attendees: next } : m));
    },
    [id],
  );

  const persistProposedGlossary = useCallback(
    async (next: ProposedTerm[]) => {
      if (!id) return;
      let prev: ProposedTerm[] = [];
      setProposedGlossary((p) => {
        prev = p;
        return next;
      });
      const { error: e } = await supabase
        .from("meetings")
        .update({ proposed_glossary: next })
        .eq("id", id);
      if (e) {
        setProposedGlossary(prev);
        setError(e.message);
      } else setMeeting((m) => (m ? { ...m, proposed_glossary: next } : m));
    },
    [id],
  );

  const handleEditAttendee = useCallback(
    (index: number, patch: Partial<ProposedAttendee>) => {
      const next = proposedAttendees.map((a, i) =>
        i === index ? { ...a, ...patch } : a,
      );
      void persistProposedAttendees(next);
    },
    [proposedAttendees, persistProposedAttendees],
  );

  const handleRemoveAttendee = useCallback(
    (index: number) => {
      const next = proposedAttendees.filter((_, i) => i !== index);
      void persistProposedAttendees(next);
    },
    [proposedAttendees, persistProposedAttendees],
  );

  const handleEditTerm = useCallback(
    (index: number, patch: Partial<ProposedTerm>) => {
      const next = proposedGlossary.map((t, i) =>
        i === index ? { ...t, ...patch } : t,
      );
      void persistProposedGlossary(next);
    },
    [proposedGlossary, persistProposedGlossary],
  );

  const handleRemoveTerm = useCallback(
    (index: number) => {
      const next = proposedGlossary.filter((_, i) => i !== index);
      void persistProposedGlossary(next);
    },
    [proposedGlossary, persistProposedGlossary],
  );

  const handleReinsertAttendee = useCallback(
    (attendee: ProposedAttendee) => {
      setProposedAttendees((prev) => {
        const next = [...prev, attendee];
        void persistProposedAttendees(next);
        return next;
      });
    },
    [persistProposedAttendees],
  );

  const handleReinsertTerm = useCallback(
    (term: ProposedTerm) => {
      setProposedGlossary((prev) => {
        const next = [...prev, term];
        void persistProposedGlossary(next);
        return next;
      });
    },
    [persistProposedGlossary],
  );

  // Display-only filter: strip proposed values that are on the project's
  // ignore list so they are never re-suggested after the user dismissed
  // them. The STORED arrays (proposedAttendees / proposedGlossary) are
  // NOT mutated — only these render-time aliases differ from the raw data.
  // When the meeting has no project, both sets are empty and the filter
  // is a no-op (all items pass through).
  // Placed here (before early returns) to satisfy Rules of Hooks: the
  // four translation useCallbacks below must be called unconditionally.
  const displayAttendees = filterIgnored(proposedAttendees, (a) => a.display_name, ignoredNameSet);
  const displayTerms = filterIgnored(proposedGlossary, (t) => t.term, ignoredTermSet);

  // Index-translation helpers: the cards receive displayAttendees / displayTerms
  // and call onEdit / onRemove with indices into THOSE arrays. But
  // persistProposedAttendees / persistProposedGlossary work on the FULL stored
  // arrays (proposedAttendees / proposedGlossary). We translate the display-index
  // → full-array index by identity (object reference equality in filterIgnored
  // preserves the original objects), so indexOf gives the full-array position.
  const handleEditAttendeeDisplay = useCallback(
    (displayIdx: number, patch: Partial<ProposedAttendee>) => {
      const item = displayAttendees[displayIdx];
      const fullIdx = proposedAttendees.indexOf(item);
      if (fullIdx === -1) return;
      handleEditAttendee(fullIdx, patch);
    },
    [displayAttendees, proposedAttendees, handleEditAttendee],
  );

  const handleRemoveAttendeeDisplay = useCallback(
    (displayIdx: number) => {
      const item = displayAttendees[displayIdx];
      const fullIdx = proposedAttendees.indexOf(item);
      if (fullIdx === -1) return;
      handleRemoveAttendee(fullIdx);
    },
    [displayAttendees, proposedAttendees, handleRemoveAttendee],
  );

  const handleEditTermDisplay = useCallback(
    (displayIdx: number, patch: Partial<ProposedTerm>) => {
      const item = displayTerms[displayIdx];
      const fullIdx = proposedGlossary.indexOf(item);
      if (fullIdx === -1) return;
      handleEditTerm(fullIdx, patch);
    },
    [displayTerms, proposedGlossary, handleEditTerm],
  );

  const handleRemoveTermDisplay = useCallback(
    (displayIdx: number) => {
      const item = displayTerms[displayIdx];
      const fullIdx = proposedGlossary.indexOf(item);
      if (fullIdx === -1) return;
      handleRemoveTerm(fullIdx);
    },
    [displayTerms, proposedGlossary, handleRemoveTerm],
  );

  // Load active projects once the meeting (and its workspace_id) is known.
  // Used by the project assign control in the header area (Task 3).
  useEffect(() => {
    if (!meeting?.workspace_id) return;
    let cancelled = false;
    loadActiveProjects(meeting.workspace_id)
      .then((p) => { if (!cancelled) setAvailableProjects(p); })
      .catch(() => { /* best-effort; dropdown shows current project only */ });
    return () => { cancelled = true; };
  }, [meeting?.workspace_id]);

  // Resolve the assigned project's display name. Prefer the active-projects
  // list; if the project isn't there (archived), fetch its name directly so the
  // transcript header shows a readable name + working link instead of a raw UUID.
  useEffect(() => {
    const pid = meeting?.project_id ?? null;
    if (!pid) { setAssignedProjectName(null); return; }
    const fromActive = availableProjects.find((p) => p.id === pid)?.name;
    if (fromActive) { setAssignedProjectName(fromActive); return; }
    let cancelled = false;
    supabase
      .from("projects")
      .select("name")
      .eq("id", pid)
      .maybeSingle()
      .then(({ data }) => { if (!cancelled) setAssignedProjectName(data?.name ?? null); });
    return () => { cancelled = true; };
  }, [meeting?.project_id, availableProjects]);

  // Open the reassign picker, staged on the current assignment.
  const openProjectPicker = useCallback(() => {
    setNewProjectName("");
    setNewProjectMode(false);
    setPendingProjectId(meeting?.project_id ?? null);
    setProjectPickerOpen(true);
  }, [meeting?.project_id]);

  // Assign (or reassign) the current meeting to a project. Calls the server
  // endpoint, updates local state, and surfaces a promotion toast.
  const handleAssignProject = useCallback(
    async (projectId: string | null, projectName: string) => {
      if (!id || !meeting) return;
      setAssigningProject(true);
      setError(null);
      try {
        const res = await assignMeetingToProject(id, projectId);
        // Flip local meeting state so the proposed cards immediately respond
        // (they key off meeting.project_id).
        setMeeting((m) => m ? { ...m, project_id: projectId } : m);
        if (projectId) {
          setPromotionToast(
            res.promotion_warning ??
              promotionToastText(res.attendees_promoted, res.terms_promoted, projectName),
          );
        }
        setProjectPickerOpen(false);
        setNewProjectMode(false);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setAssigningProject(false);
      }
    },
    [id, meeting],
  );

  // Create a new project inline and immediately assign the meeting to it.
  const handleCreateAndAssign = useCallback(async () => {
    if (!meeting?.workspace_id) return;
    const trimmedName = newProjectName.trim();
    if (!trimmedName) return;
    setCreatingProject(true);
    setError(null);
    try {
      const { data, error: insertErr } = await supabase
        .from("projects")
        .insert({ workspace_id: meeting.workspace_id, name: trimmedName })
        .select("id, name")
        .single();
      if (insertErr) throw insertErr;
      const newProject = data as { id: string; name: string };
      // Add to local available-projects list so the dropdown reflects it.
      setAvailableProjects((p) => [...p, { id: newProject.id, name: newProject.name }].sort(
        (a, b) => a.name.localeCompare(b.name),
      ));
      setNewProjectName("");
      await handleAssignProject(newProject.id, newProject.name);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCreatingProject(false);
    }
  }, [meeting?.workspace_id, newProjectName, handleAssignProject]);

  // reverseEvent reverses a single meeting_events row by event_type.
  // Extracted so it can be shared by executeUndo (global Undo button),
  // the Cancel path inside TranscriptView, and the edit_session undo
  // case below. Throws on failure — callers decide whether to surface
  // or continue.
  const reverseEvent = useCallback(async (ev: MeetingEvent) => {
    if (!id) return;
    const p = (ev.payload ?? {}) as MeetingEventPayload;
    switch (ev.event_type) {
      case "title_change": {
        const oldTitle = (p.old_title as string | null) ?? null;
        const { error: e } = await supabase.from("meetings").update({ title: oldTitle }).eq("id", id);
        if (e) throw e;
        setMeeting((m) => m ? { ...m, title: oldTitle } : m);
        break;
      }
      case "speaker_label_override": {
        const { data: row, error: readErr } = await supabase
          .from("transcripts").select("speaker_map").eq("meeting_id", id).maybeSingle();
        if (readErr) throw readErr;
        const label = p.label as string;
        const oldName = (p.old_name as string | null) ?? null;
        type MapEntry = { name: string | null; confidence: number; raw_label: string };
        const currentMap = (row?.speaker_map ?? {}) as Record<string, MapEntry>;
        const entry = currentMap[label];
        if (entry) {
          const newMap = { ...currentMap, [label]: { ...entry, name: oldName, confidence: oldName ? 1.0 : 0 } };
          const { error: writeErr } = await supabase.from("transcripts").update({ speaker_map: newMap }).eq("meeting_id", id);
          if (writeErr) throw writeErr;
        }
        break;
      }
      case "span_attribution_add":
        await removeSpanAttribution(id, p.id as string);
        break;
      case "span_attribution_remove":
        await appendSpanAttribution(id, p.entry as SpanAttribution);
        break;
      case "paragraph_split":
        await removeParagraphSplit(id, p.id as string);
        break;
      case "paragraph_rejoin":
        await appendParagraphSplit(id, p.entry as ParagraphSplit);
        break;
      case "text_correction_accept":
        await setCorrectionStatus(p.correction_id as string, "dismissed");
        break;
      case "text_correction_dismiss":
        await setCorrectionStatus(p.correction_id as string, "accepted");
        break;
      case "bookmark_set": {
        // Multi-bookmark undo (migration 0022): the inverse of "add at_ms"
        // is "remove at_ms" from the array. Read-modify-write because the
        // array may have other bookmarks added/removed since this event.
        const bp = ev.payload as BookmarkSetPayload | null;
        const atMs = bp?.at_ms;
        if (typeof atMs !== "number") break;
        const { data: row, error: readErr } = await supabase
          .from("meetings").select("bookmarks_at_ms").eq("id", id).maybeSingle();
        if (readErr) throw readErr;
        const current = (row?.bookmarks_at_ms ?? []) as number[];
        const next = current.filter((ms) => ms !== atMs);
        const { error: bErr } = await supabase
          .from("meetings").update({ bookmarks_at_ms: next }).eq("id", id);
        if (bErr) throw bErr;
        setMeeting((m) => m ? { ...m, bookmarks_at_ms: next } : m);
        break;
      }
      case "bookmark_clear": {
        // Multi-bookmark undo: the inverse of "remove at_ms" is "add at_ms"
        // back to the array. Re-add in sorted order (matches toggleBookmark).
        const bp = ev.payload as BookmarkClearPayload | null;
        const atMs = bp?.at_ms;
        if (typeof atMs !== "number") break;
        const { data: row, error: readErr } = await supabase
          .from("meetings").select("bookmarks_at_ms").eq("id", id).maybeSingle();
        if (readErr) throw readErr;
        const current = (row?.bookmarks_at_ms ?? []) as number[];
        const next = current.includes(atMs)
          ? current
          : [...current, atMs].sort((a, b) => a - b);
        const { error: bErr } = await supabase
          .from("meetings").update({ bookmarks_at_ms: next }).eq("id", id);
        if (bErr) throw bErr;
        setMeeting((m) => m ? { ...m, bookmarks_at_ms: next } : m);
        break;
      }
      case "paragraph_revert": {
        // Restore every overlay that the Reset removed: corrections,
        // span attributions, paragraph splits, and the bookmark.
        const rp = ev.payload as ParagraphRevertPayload | null;
        if (!rp) break;

        // Restore corrections: flip each back to 'accepted' (or the
        // original status stored in the snapshot if it differs).
        for (const c of rp.removed_corrections ?? []) {
          try {
            const originalStatus = (c.status as string) === "dismissed" ? "accepted" : (c.status as "accepted" | "dismissed");
            await setCorrectionStatus(c.id, originalStatus);
          } catch (cErr) {
            // eslint-disable-next-line no-console
            console.error(`paragraph_revert undo: failed to restore correction ${c.id}:`, cErr);
            setError(cErr instanceof Error ? cErr.message : String(cErr));
          }
        }

        // Restore span attributions.
        for (const sa of rp.removed_span_attributions ?? []) {
          try {
            await appendSpanAttribution(id, sa);
          } catch (saErr) {
            // eslint-disable-next-line no-console
            console.error(`paragraph_revert undo: failed to restore span attribution ${sa.id}:`, saErr);
            setError(saErr instanceof Error ? saErr.message : String(saErr));
          }
        }

        // Restore paragraph splits.
        for (const ps of rp.removed_paragraph_splits ?? []) {
          try {
            await appendParagraphSplit(id, ps);
          } catch (psErr) {
            // eslint-disable-next-line no-console
            console.error(`paragraph_revert undo: failed to restore paragraph split ${ps.id}:`, psErr);
            setError(psErr instanceof Error ? psErr.message : String(psErr));
          }
        }

        // Restore every bookmark position that the Reset removed (multi-
        // bookmark model). Read-modify-write so positions toggled in/out
        // since the Reset are preserved.
        const toRestore = rp.removed_bookmarks_at_ms ?? [];
        if (toRestore.length > 0) {
          const { data: row, error: readErr } = await supabase
            .from("meetings").select("bookmarks_at_ms").eq("id", id).maybeSingle();
          if (readErr) {
            // eslint-disable-next-line no-console
            console.error("paragraph_revert undo: failed to read bookmarks:", readErr);
            setError(readErr.message);
          } else {
            const current = (row?.bookmarks_at_ms ?? []) as number[];
            const merged = Array.from(new Set([...current, ...toRestore])).sort(
              (a, b) => a - b,
            );
            const { error: bErr } = await supabase
              .from("meetings").update({ bookmarks_at_ms: merged }).eq("id", id);
            if (bErr) {
              // eslint-disable-next-line no-console
              console.error("paragraph_revert undo: failed to restore bookmarks:", bErr);
              setError(bErr.message);
            } else {
              setMeeting((m) => m ? { ...m, bookmarks_at_ms: merged } : m);
            }
          }
        }
        break;
      }
      // ---------------------------------------------------------------
      // WYSIWYG sub-event reverse handlers (Task 1.12).
      // These are dispatched recursively by the edit_session case below
      // when it walks sub_event_ids in reverse order.
      //
      // Idempotency invariant: supabase-js delete() and update() succeed
      // with zero rows affected when the WHERE clause matches nothing —
      // they do NOT throw. This is Postgres behavior, surfaced faithfully
      // by supabase-js. Never add .throwOnError() on these calls.
      // ---------------------------------------------------------------
      case "correction_create": {
        // Reverse: delete the correction that was created.
        // No-op if the row is already gone (e.g., non-LIFO undo ordering).
        const correctionId = p.correction_id as string;
        await supabase.from("corrections").delete().eq("id", correctionId);
        break;
      }
      case "correction_dismiss": {
        // Reverse: re-accept the correction that was dismissed.
        // No-op if the row no longer exists.
        const correctionId = p.correction_id as string;
        await supabase
          .from("corrections")
          .update({ status: "accepted" })
          .eq("id", correctionId);
        break;
      }
      case "flag_revert": {
        // Reverse: restore the flag to its post_status_at_revert_time
        // (i.e., what the flag's status was after the revert ran —
        // that is the state we are restoring when undoing a flag_revert).
        // No-op if the flag row no longer exists.
        const flagId = p.flag_id as string;
        const postStatus = p.post_status_at_revert_time as string;
        await supabase
          .from("flags")
          .update({ status: postStatus })
          .eq("id", flagId);
        break;
      }
      case "edit_session": {
        // Reverse every sub-event in reverse chronological order.
        // loadEventsByIds silently omits missing rows — any sub-event
        // that no longer exists in meeting_events is simply skipped
        // (idempotency for the wrapper level).
        const ep = ev.payload as EditSessionPayload | null;
        const subIds = ep?.sub_event_ids ?? [];
        if (subIds.length === 0) break;
        const subEvents = await loadEventsByIds(subIds);
        // Reverse order: undo last-action-first.
        const reversed = [...subEvents].reverse();
        for (const sub of reversed) {
          try {
            // Use the ref to avoid stale-closure recursion: reverseEventRef
            // always points to the latest version of this function.
            await reverseEventRef.current(sub);
          } catch (subErr) {
            // Log loudly but continue — partial state is acceptable.
            // eslint-disable-next-line no-console
            console.error(
              `edit_session undo: reverseEvent failed for sub-event ${sub.id} (${sub.event_type}):`,
              subErr,
            );
          }
        }
        break;
      }
      default:
        throw new Error(`Don't know how to undo "${ev.event_type}"`);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ref-mirror for reverseEvent so the edit_session case can call it
  // recursively without stale-closure issues.
  const reverseEventRef = useRef(reverseEvent);
  useEffect(() => {
    reverseEventRef.current = reverseEvent;
  }, [reverseEvent]);

  const executeUndo = useCallback(async () => {
    if (!lastUserEvent || !id || undoing) return;
    // Lock invariant: a locked meeting must not accept any mutations,
    // including Undo. The button and Cmd/Ctrl+Z handler both gate on
    // isLocked, but this is the load-bearing backstop -- any other call
    // site (future keyboard shortcut, programmatic invocation) still
    // gets the guarantee.
    if (meeting?.locked_at) return;
    setUndoing(true);
    setError(null);
    try {
      await reverseEvent(lastUserEvent);

      try {
        if (meeting) {
          await logUserEvent({
            meetingId: id,
            workspaceId: meeting.workspace_id,
            eventType: "undo_action",
            payload: { undone_event_type: lastUserEvent.event_type, undone_event_id: lastUserEvent.id },
          });
        }
      } catch { /* best-effort */ }

      // Force-refresh: the reverseEvent for edit_session reverses MULTIPLE
      // sub-events sequentially, each writing to a different table. Relying
      // on Realtime to deliver each one separately is unreliable; pull the
      // canonical state in one shot so the visual reflects all reversals.
      try { await refreshAll(); } catch { /* best-effort */ }

      const next = await loadLastUserEvent(id);
      setLastUserEvent(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUndoing(false);
    }
  }, [id, meeting, lastUserEvent, undoing, reverseEvent, refreshAll]);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (!(e.ctrlKey || e.metaKey) || e.key !== "z" || e.shiftKey) return;
      const active = document.activeElement;
      const isTyping =
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        (active instanceof HTMLElement && active.isContentEditable);
      if (isTyping) return;
      e.preventDefault();
      void executeUndo();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [executeUndo]);

  const reviewItems = useMemo(
    () => aggregateReviewItems(
      flags,
      transcript?.span_attributions ?? [],
    ),
    [flags, transcript?.span_attributions],
  );

  // Summary-read marker (spec §4): per-meeting localStorage flag, set when
  // the Summary tab is opened while a summary exists.
  //
  // Hardening (Task 13 / orchestrator add-on): react-router reuses this
  // component instance on meeting→meeting navigation. `transcript` is NOT
  // cleared synchronously when `id` changes — `loadTranscript` is async.
  // To prevent the WRITE from accidentally stamping a NEW meeting's id as
  // "read" while the OLD transcript is still in state, we gate the write on
  // `meeting?.id === id` (the loaded meeting row belongs to the current URL id).
  // The read-from-localStorage effect also resets summaryRead on id change
  // so the state is clean while the fresh transcript loads.
  const [summaryRead, setSummaryRead] = useState(false);
  useEffect(() => { setSummaryRead(id ? readSummaryRead(id) : false); }, [id]);
  // Reset per-meeting in-flight rework state on id change.
  // Defensive guard: prevents a stale reworking/regenFiredAt from a previous
  // meeting leaking into a new one when navigating between meetings via the
  // same /meetings/:id route (same class as the summaryRead guard above).
  useEffect(() => {
    setReworking(null);
    setRegenFiredAt(null);
    setReworkSelecting(false);
    setReworkRange(null);
  }, [id]);
  useEffect(() => {
    // Guard: only write when the loaded meeting matches the URL param so a
    // stale transcript from a previous /meetings/:id never marks the NEW id read.
    if (activeTab === "summary" && transcript?.summary && id && !summaryRead && meeting?.id === id) {
      writeSummaryRead(id);
      setSummaryRead(true);
    }
  }, [activeTab, transcript?.summary, id, summaryRead, meeting?.id]);

  const journey = useMemo(
    () =>
      meeting
        ? deriveJourney(
            { status: meeting.status, locked_at: meeting.locked_at, context: meeting.context },
            {
              hasTranscript: !!transcript,
              hasSummary: !!transcript?.summary,
              summaryRead,
              openProposalCount: displayAttendees.length + displayTerms.length,
              hasProject: !!meeting.project_id,
              reviewItems,
              integrity: transcript?.integrity ?? null,
            },
          )
        : null,
    [meeting, transcript, summaryRead, displayAttendees, displayTerms, reviewItems],
  );

  const handleRegenerateSummary = useCallback(async () => {
    if (!id || regenFiredAt) return;
    setError(null);
    try {
      await regenerateSummary(id);
      setRegenFiredAt(new Date().toISOString());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [id, regenFiredAt]);

  const fireRework = useCallback(async (hint: string | null) => {
    if (!id || !reworkRange) return;
    setReworkBusy(true); setError(null);
    try {
      await reworkSection(id, { start_ms: reworkRange.startMs, end_ms: reworkRange.endMs, hint });
      setReworking({ startMs: reworkRange.startMs, endMs: reworkRange.endMs, firedAt: new Date().toISOString() });
      setReworkRange(null); setReworkSelecting(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setReworkBusy(false); }
  }, [id, reworkRange]);

  // Checklist deep-links (spec §4): chips are projections of page state and
  // open the surface where the item is handled.
  const handleChecklistClick = useCallback((item: ChecklistItem) => {
    if (item.target === "summary") { setActiveTab("summary"); return; }
    if (item.target === "project_picker") { openProjectPicker(); return; }
    if (item.target === "regenerate_summary") { void handleRegenerateSummary(); return; }
    if (item.target === "start_over") { setStartOverOpen(true); return; }
    // target === "rail": open + expand the rail. Only the flag-backed chips
    // (speakers / mishearings) also focus a review item; names_terms lands on
    // the ProposedAttendees/ProposedGlossary cards in the rail — focusing a
    // flag item there would scroll to an unrelated mishearing.
    setRailCollapsed(false);
    writeRailCollapsed(false);
    setMobileRailOpen(true);
    if (item.id === "speakers" || item.id === "mishearings") {
      const kinds = item.id === "speakers" ? SPEAKER_SUGGESTION_KINDS : MISHEARING_KINDS;
      const first = reviewItems
        .filter((i) => i.status === "pending" && kinds.has(i.kind) && i.start_ms !== null)
        .sort((a, b) => (a.start_ms ?? 0) - (b.start_ms ?? 0))[0];
      if (first?.start_ms != null) reviewPanelRef.current?.expandAndFocusItem(first.start_ms);
    }
  }, [openProjectPicker, reviewItems, handleRegenerateSummary]);

  // Client-side completion watcher for the gentle loops (regenerate / rework).
  // Status stays 'ready' for these, so the IN_FLIGHT poll doesn't fire —
  // poll until the job's meeting_events row lands, then refresh + clear.
  useEffect(() => {
    if (!id || (!regenFiredAt && !reworking)) return;
    const interval = setInterval(async () => {
      try {
        if (regenFiredAt && await hasMeetingEventSince(id, ["summary_regenerated", "summary_failed"], regenFiredAt)) {
          setRegenFiredAt(null);
          try { await refreshAll(); } catch { /* best-effort */ }
        }
        if (reworking && await hasMeetingEventSince(id, ["rework_section", "rework_failed"], reworking.firedAt)) {
          setReworking(null);
          try { await refreshAll(); } catch { /* best-effort */ }
          try { await loadFlags(); } catch { /* best-effort */ }
        }
      } catch { /* best-effort */ }
    }, 5000);
    return () => clearInterval(interval);
  }, [id, regenFiredAt, reworking, refreshAll, loadFlags]);

  if (error) return <p className="error">{error}</p>;
  if (!meeting) return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 16 }}>
      <Skeleton height={28} width="55%" />
      <Skeleton height={16} width="35%" />
      <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 10 }}>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} height={70} style={{ borderRadius: 8 }} />
        ))}
      </div>
    </div>
  );

  // Review vs locked mode. SpeakerLabel + Re-annotate + Resubmit + the
  // click-to-edit speaker override all branch on this single derived value.
  const isLocked = !!meeting.locked_at;

  // getOriginalText extracts the verbatim text in [startMs, endMs] for
  // pre-filling the correction popover when invoked from a flag. Token-
  // atomic: a token is included if its start_ms >= startMs AND its
  // end_ms <= endMs (the flag's span IS the utterance span per sentinel
  // calibration, so this matches naturally). Empty string when the
  // verbatim isn't loaded yet -- the popover renders the empty state.
  const getOriginalText = (startMs: number, endMs: number): string => {
    const tokens = transcript?.payload?.transcript?.tokens ?? [];
    return tokens
      .filter((t) => t.start_ms >= startMs && t.end_ms <= endMs)
      .map((t) => t.text)
      .join("")
      .trim();
  };

  return (
    <>
      <BackToTop />

      {/* ── Studio header ─────────────────────────────────────────────── */}
      <MeetingHeader
        meeting={meeting}
        isLocked={isLocked}
        onTitleChange={(next) => setMeeting({ ...meeting, title: next })}
        onRecordedAtChange={(nextIso) =>
          setMeeting((m) => (m ? { ...m, recorded_at: nextIso } : m))
        }
        headerRef={stickyHeaderRef}
        onToggleReview={() => setMobileRailOpen((o) => !o)}
        onResubmitStart={() => {
          const prevStatus = meeting.status;
          setMeeting({ ...meeting, status: "uploaded" });
          return prevStatus;
        }}
        onResubmitError={(prevStatus, e) => {
          setMeeting({ ...meeting, status: prevStatus });
          setError(e instanceof Error ? e.message : String(e));
        }}
      />

      {/* ── Journey strip — the lifecycle band (replaces the status banner
          and the error/try-again row; spec §4 + §9) ─────────────────── */}
      {journey && (
        <JourneyStrip
          journey={journey}
          meetingId={meeting.id}
          status={meeting.status}
          lockedAt={meeting.locked_at}
          errorMessage={meeting.error_message}
          locking={locking}
          updating={reannotating}
          canTryAgain={isRecoverableOrphan(meeting, transcript)}
          reworking={reworking}
          lockedDate={meeting.locked_at ? new Date(meeting.locked_at).toLocaleDateString() : null}
          onTryAgain={async () => {
            const prevStatus = meeting.status;
            setMeeting({ ...meeting, status: "uploaded", error_message: null });
            try {
              await resubmitMeeting(meeting.id);
            } catch (e) {
              setMeeting({ ...meeting, status: prevStatus });
              setError(e instanceof Error ? e.message : String(e));
            }
          }}
          onLockToggle={async () => {
            if (!id) return;
            setLocking(true);
            setError(null);
            try {
              if (isLocked) await unlockMeeting(id);
              else await lockMeeting(id);
              try { await refreshAll(); } catch { /* best-effort */ }
            } catch (e) {
              setError(e instanceof Error ? e.message : String(e));
            } finally {
              setLocking(false);
            }
          }}
          onOpenUpdateAnalysis={() => {
            setExtraContext("");
            setReannotateDialogOpen(true);
          }}
          onOpenReworkSection={() => { setActiveTab("transcript"); setReworkSelecting(true); }}
          onChecklistClick={handleChecklistClick}
          onScrollToBriefingReview={() => {
            document.querySelector("#ctx-review-text")?.scrollIntoView({ behavior: "smooth", block: "center" });
          }}
        />
      )}
      {meeting.status !== "error" && meeting.error_message && (
        <p className="error" style={{ marginTop: 10, fontSize: 13 }}>{meeting.error_message}</p>
      )}

      {/* ── Project assignment control ────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginTop: 10,
          fontSize: 13,
          color: "var(--text-muted)",
        }}
      >
        <span style={{ fontWeight: 500 }}>Project:</span>
        {/* No lock gate: moving a transcript between projects is organization,
            not a content edit, so it stays available even when locked (same as
            the title). The lock freezes the transcript's wording/speakers. */}
        {projectPickerOpen ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            {/* New-project inline input (shown when "new" mode is active) */}
            {newProjectMode || creatingProject ? (
              <>
                <input
                  type="text"
                  autoFocus
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); void handleCreateAndAssign(); }
                    if (e.key === "Escape") { setNewProjectName(""); setNewProjectMode(false); setProjectPickerOpen(false); }
                  }}
                  placeholder="New project name"
                  disabled={creatingProject}
                  style={{
                    padding: "3px 8px",
                    fontSize: 13,
                    width: 180,
                    background: "var(--surface-2)",
                    border: "1px solid var(--accent)",
                    borderRadius: 4,
                    color: "var(--text)",
                  }}
                />
                <button
                  type="button"
                  className="secondary"
                  onClick={() => void handleCreateAndAssign()}
                  disabled={creatingProject || !newProjectName.trim()}
                  style={{ padding: "3px 10px", fontSize: 12 }}
                >
                  {creatingProject ? "Creating…" : "Create & assign"}
                </button>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => { setNewProjectName(""); setNewProjectMode(false); setProjectPickerOpen(false); }}
                  disabled={creatingProject}
                  style={{ padding: "3px 8px", fontSize: 12 }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <select
                  autoFocus
                  value={pendingProjectId ?? ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "__new__") {
                      // Switch to the inline-create input (decoupled from the
                      // name text, which starts empty).
                      setNewProjectMode(true);
                      setNewProjectName("");
                      return;
                    }
                    // Stage the choice only — does NOT assign. The user must
                    // click "Assign" to commit (guards against accidental
                    // reassignment + its name/term promotion).
                    setPendingProjectId(val || null);
                  }}
                  disabled={assigningProject}
                  style={{
                    padding: "3px 8px",
                    fontSize: 13,
                    background: "var(--surface-2)",
                    border: "1px solid var(--border)",
                    borderRadius: 4,
                    color: "var(--text)",
                    cursor: "pointer",
                  }}
                >
                  <option value="">Unassigned</option>
                  {availableProjects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                  <option value="__new__">+ New project…</option>
                </select>
                <button
                  type="button"
                  onClick={() => {
                    const name =
                      availableProjects.find((p) => p.id === pendingProjectId)?.name ??
                      "the project";
                    void handleAssignProject(pendingProjectId, name);
                  }}
                  disabled={
                    assigningProject || pendingProjectId === (meeting.project_id ?? null)
                  }
                  title="Apply this project assignment"
                  style={{ padding: "3px 10px", fontSize: 12 }}
                >
                  {assigningProject
                    ? "Assigning…"
                    : pendingProjectId
                      ? "Assign"
                      : "Set to Unassigned"}
                </button>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => setProjectPickerOpen(false)}
                  disabled={assigningProject}
                  style={{ padding: "3px 8px", fontSize: 12 }}
                >
                  Cancel
                </button>
              </>
            )}
          </span>
        ) : meeting.project_id ? (
          // Assigned: the project NAME links to its page; a small "Change"
          // opens the reassign picker — so clicking the name navigates rather
          // than hijacking into reassignment.
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Link
              to={`/projects/${meeting.project_id}`}
              style={{ color: "var(--accent)", fontWeight: 500 }}
              title="Open this transcript's project"
            >
              {assignedProjectName ?? "project"}
            </Link>
            <button
              type="button"
              className="secondary"
              onClick={openProjectPicker}
              title="Move this transcript to a different project"
              style={{ padding: "2px 8px", fontSize: 11 }}
            >
              Change
            </button>
          </span>
        ) : (
          // Unassigned: a plain control that opens the assign picker.
          <button
            type="button"
            onClick={openProjectPicker}
            title="Assign this transcript to a project"
            style={{
              background: "none",
              border: "1px solid var(--border)",
              borderRadius: 4,
              padding: "2px 8px",
              fontSize: 12,
              color: "var(--text-muted)",
              cursor: "pointer",
              fontStyle: "italic",
            }}
          >
            Assign…
          </button>
        )}
      </div>

      {/* ── Studio body: tabs + main content + rail ─────────────────── */}
      {/* Tabs bar */}
      <Tabs
        tabs={[
          { id: "summary",    label: t("meeting.tabSummary") },
          { id: "transcript", label: t("meeting.tabTranscript") },
        ]}
        activeTab={activeTab}
        onTabChange={(id) => setActiveTab(id as "summary" | "transcript")}
        className="meeting-tabs"
      />

      {/* Main layout: [content column | review rail]. Columns stretch to
          equal height (default align-items) so the docked player — sticky
          bottom: 0, LAST CHILD of the content column — stays pinned even
          when the rail is the taller column. The dock lives inside the
          content column (not after the row) so it spans the column's
          width, not the rail's; main.page is the scroll container that
          sticky engages against (see global.css .app). */}
      <div style={{ display: "flex", gap: 0, marginTop: 0 }}>
        {/* ── Content column ──────────────────────────────────────── */}
        <div data-testid="meeting-main-column" style={{ flex: 1, minWidth: 0 }}>

          {/* Playback error card */}
          {playbackError && !playbackUrl && (
            <div
              className="card"
              style={{
                marginTop: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                borderColor: "var(--danger, #b91c1c)",
              }}
            >
              <div style={{ fontSize: 13 }}>
                <strong style={{ color: "var(--danger, #b91c1c)" }}>
                  Audio failed to load.
                </strong>{" "}
                <span className="muted" style={{ fontSize: 12 }}>
                  {playbackError}
                </span>
              </div>
              <button
                type="button"
                className="secondary"
                onClick={() => setPlaybackAttempt((n) => n + 1)}
              >
                Retry
              </button>
            </div>
          )}

          {!reworkSelecting && <MeetingSynopsis synopsis={attributionSynopsis} />}

          {/* Pre-Soniox context review card — only shown when the pipeline
              paused to let the user curate the parsed context bundle before
              sending the audio to Soniox. Replaces the transcript card. */}
      {meeting.status === "awaiting_context_review" && (
        <div className="card" style={{ marginTop: 16 }}>
          <h3 style={{ marginTop: 0 }}>Review before transcribing</h3>
          <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
            We've parsed your notes into a context bundle. Fix any name spelling
            issues, remove wrong attendees, or adjust the prose before the recording
            is sent for transcription. What you see here is what the transcription
            engine will use.
          </p>

          {/* General key/value pairs — often empty; show if present */}
          {contextReviewGeneral.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                Meeting details
              </label>
              {contextReviewGeneral.map((pair, idx) => (
                <div key={idx} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                  <input
                    type="text"
                    value={pair.key}
                    onChange={(e) => {
                      const next = [...contextReviewGeneral];
                      next[idx] = { ...next[idx], key: e.target.value };
                      setContextReviewGeneral(next);
                    }}
                    placeholder="key"
                    style={{
                      flex: "0 0 140px",
                      padding: "4px 8px",
                      background: "var(--surface-2, #21252f)",
                      color: "var(--text)",
                      border: "1px solid var(--border)",
                      borderRadius: 4,
                      fontSize: 13,
                    }}
                  />
                  <input
                    type="text"
                    value={pair.value}
                    onChange={(e) => {
                      const next = [...contextReviewGeneral];
                      next[idx] = { ...next[idx], value: e.target.value };
                      setContextReviewGeneral(next);
                    }}
                    placeholder="value"
                    style={{
                      flex: 1,
                      padding: "4px 8px",
                      background: "var(--surface-2, #21252f)",
                      color: "var(--text)",
                      border: "1px solid var(--border)",
                      borderRadius: 4,
                      fontSize: 13,
                    }}
                  />
                  <button
                    type="button"
                    className="secondary"
                    style={{ fontSize: 12, padding: "2px 8px" }}
                    onClick={() => setContextReviewGeneral(contextReviewGeneral.filter((_, i) => i !== idx))}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Terms list — names, project names, technical terms Soniox
              should spell consistently */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
              Names and terms
              <span className="muted" style={{ fontWeight: 400, marginLeft: 6 }}>
                (words the transcription engine should spell consistently)
              </span>
            </label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
              {contextReviewTerms.map((term, idx) => (
                <span
                  key={idx}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "2px 8px",
                    background: "var(--surface-2, #21252f)",
                    border: "1px solid var(--border)",
                    borderRadius: 12,
                    fontSize: 13,
                  }}
                >
                  <input
                    type="text"
                    value={term}
                    onChange={(e) => {
                      const next = [...contextReviewTerms];
                      next[idx] = e.target.value;
                      setContextReviewTerms(next);
                    }}
                    title="Click to edit this term"
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "var(--text)",
                      fontSize: 13,
                      width: `${Math.max(term.length + 2, 8)}ch`,
                      outline: "none",
                      cursor: "text",
                    }}
                    onFocus={(e) => {
                      const span = e.currentTarget.closest("span") as HTMLElement | null;
                      if (span) {
                        span.style.border = "1px solid var(--accent, #6366f1)";
                        span.style.background = "var(--surface, #181b24)";
                      }
                    }}
                    onBlur={(e) => {
                      const span = e.currentTarget.closest("span") as HTMLElement | null;
                      if (span) {
                        span.style.border = "1px solid var(--border)";
                        span.style.background = "var(--surface-2, #21252f)";
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setContextReviewTerms(contextReviewTerms.filter((_, i) => i !== idx))}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "var(--text-muted, #9ca3af)",
                      fontSize: 11,
                      padding: 0,
                      lineHeight: 1,
                    }}
                    title="Remove this term"
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="text"
                value={contextReviewTermInput}
                onChange={(e) => setContextReviewTermInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    const v = contextReviewTermInput.trim().replace(/,+$/, "");
                    if (v && !contextReviewTerms.includes(v)) {
                      setContextReviewTerms([...contextReviewTerms, v]);
                    }
                    setContextReviewTermInput("");
                  }
                }}
                placeholder="Add a name or term, press Enter"
                style={{
                  flex: 1,
                  padding: "5px 8px",
                  background: "var(--surface-2, #21252f)",
                  color: "var(--text)",
                  border: "1px solid var(--border)",
                  borderRadius: 4,
                  fontSize: 13,
                }}
              />
              <button
                type="button"
                className="secondary"
                style={{ fontSize: 13 }}
                onClick={() => {
                  const v = contextReviewTermInput.trim().replace(/,+$/, "");
                  if (v && !contextReviewTerms.includes(v)) {
                    setContextReviewTerms([...contextReviewTerms, v]);
                  }
                  setContextReviewTermInput("");
                }}
              >
                Add
              </button>
            </div>
          </div>

          {/* Free-form prose context */}
          <div style={{ marginBottom: 20 }}>
            <label
              htmlFor="ctx-review-text"
              style={{ display: "block", fontWeight: 600, fontSize: 13, marginBottom: 6 }}
            >
              Context notes
              <span className="muted" style={{ fontWeight: 400, marginLeft: 6 }}>
                (background that helps the engine understand who was in the meeting and what it was about)
              </span>
            </label>
            <textarea
              id="ctx-review-text"
              dir="auto"
              rows={5}
              value={contextReviewText}
              onChange={(e) => setContextReviewText(e.target.value)}
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: 8,
                background: "var(--surface-2, #21252f)",
                color: "var(--text, #f9fafb)",
                border: "1px solid var(--border, #374151)",
                borderRadius: 6,
                fontFamily: "inherit",
                fontSize: 14,
                resize: "vertical",
              }}
            />
          </div>

          {error && <p className="error" style={{ marginBottom: 12 }}>{error}</p>}

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button
              type="button"
              className="secondary"
              disabled={submittingContext}
              title="Send to transcription with this context as-is"
              onClick={async () => {
                if (!id || !meeting) return;
                setSubmittingContext(true);
                setError(null);
                try {
                  await submitContextForSoniox(id, {
                    general: contextReviewGeneral,
                    terms: contextReviewTerms,
                    text: contextReviewText,
                  });
                  // Optimistic flip so the journey strip updates immediately
                  // and the 5s poller starts — mirrors the Re-annotate pattern.
                  // Realtime/polling will reconcile with the real status.
                  setMeeting({ ...meeting, status: "transcribing" });
                } catch (e) {
                  setError(e instanceof Error ? e.message : String(e));
                  setSubmittingContext(false);
                }
              }}
              style={{ fontSize: 13 }}
            >
              {submittingContext ? "Sending…" : "Submit as-is"}
            </button>
            <button
              type="button"
              disabled={submittingContext}
              onClick={async () => {
                if (!id || !meeting) return;
                setSubmittingContext(true);
                setError(null);
                try {
                  await submitContextForSoniox(id, {
                    general: contextReviewGeneral,
                    terms: contextReviewTerms,
                    text: contextReviewText,
                  });
                  // Optimistic flip so the journey strip updates immediately
                  // and the 5s poller starts — mirrors the Re-annotate pattern.
                  // Realtime/polling will reconcile with the real status.
                  setMeeting({ ...meeting, status: "transcribing" });
                } catch (e) {
                  setError(e instanceof Error ? e.message : String(e));
                  setSubmittingContext(false);
                }
              }}
            >
              {submittingContext ? "Submitting…" : "Submit to transcription →"}
            </button>
          </div>
        </div>
      )}

          {/* ── Tab content ─────────────────────────────────────── */}
          {activeTab === "summary" && (
            <SummaryTab
              summary={transcript?.summary ?? null}
              recordedAt={meeting.recorded_at}
              onSeekAndSwitch={(ms) => {
                // Store the seek target; the effect above fires it once the
                // transcript tab mounts and the player is ready — no setTimeout race.
                pendingSeekRef.current = ms;
                setActiveTab("transcript");
              }}
              canRegenerate={meeting.status === "ready" && !isLocked}
              regenerating={regenFiredAt !== null}
              onRegenerate={() => void handleRegenerateSummary()}
            />
          )}

          {activeTab === "transcript" && (
            <TranscriptTab>
              <div className="card" style={{ marginTop: 16 }}>
                <h3 style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 10 }}>
                  Transcript
                  {lastUserEvent && (
                    <button
                      type="button"
                      className="secondary"
                      disabled={undoing || isLocked}
                      onClick={() => void executeUndo()}
                      title={
                        isLocked
                          ? "Unlock the meeting first."
                          : `Undo: ${describeEvent(lastUserEvent)}`
                      }
                      style={{ fontSize: 12, padding: "3px 10px", marginInlineStart: "auto" }}
                    >
                      {undoing ? "Undoing…" : `↩ Undo: ${describeEvent(lastUserEvent)}`}
                    </button>
                  )}
                  {/* "Hide my edits" toggle */}
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => setSourceToggle((v) => !v)}
                    title={
                      viewingSource
                        ? "Bring back your corrections, splits, and speaker overrides. (Or hold Alt to peek the other view.)"
                        : "Show the raw transcript before your edits. Hold Alt to peek temporarily."
                    }
                    style={{
                      fontSize: 12,
                      padding: "3px 10px",
                      ...(viewingSource
                        ? {
                            background: "var(--accent)",
                            color: "var(--on-accent)",
                            border: "1px solid var(--accent)",
                            fontWeight: 600,
                          }
                        : {}),
                    }}
                  >
                    <Icon name="eye" size={14} aria-hidden />
                    {viewingSource ? " Show my edits" : " Hide my edits"}
                  </button>
                  {meeting.status === "ready" && !isLocked && !viewingSource && !reworkSelecting && (
                    <button type="button" className="secondary" style={{ fontSize: 12, padding: "3px 10px" }}
                      onClick={() => setReworkSelecting(true)}
                      title="Tap the first and last paragraph of a stretch to re-check it.">
                      {t("rework.entry")}
                    </button>
                  )}
                </h3>
                {!transcript ? (
                  <p className="muted">
                    {meeting.status === "ready"
                      ? "Transcript row missing."
                      : meeting.status === "awaiting_context_review"
                      ? "Transcription will start after you submit the context above."
                      : "Transcript not ready yet — this page updates automatically."}
                  </p>
                ) : (
                  <TranscriptView
                    transcript={transcript}
                    recordedAt={meeting.recorded_at}
                    isLocked={isLocked}
                    onSeek={seekTo}
                    meetingId={meeting.id}
                    workspaceId={meeting.workspace_id}
                    projectId={meeting.project_id ?? null}
                    playbackTimeRef={playbackTimeRef}
                    corrections={corrections}
                    editRequest={editRequest}
                    paragraphSplits={transcript.paragraph_splits ?? []}
                    bookmarksAtMs={meeting.bookmarks_at_ms}
                    flags={flags}
                    onFocusFlag={(startMs) => reviewPanelRef.current?.expandAndFocusItem(startMs)}
                    onInlineEditOpen={() => playerRef.current?.pause()}
                    onError={setError}
                    onForceRefresh={refreshAll}
                    viewingSource={viewingSource}
                    reworkSelecting={reworkSelecting && meeting.status === "ready" && !isLocked}
                    onReworkRangeChosen={(startMs, endMs, paragraphs) => setReworkRange({ startMs, endMs, paragraphs })}
                    onReworkCancel={() => setReworkSelecting(false)}
                    following={followState.following}
                    programmaticScrollRef={programmaticScrollRef}
                    onFollowDisengage={() => dispatchFollow("manual_scroll")}
                  />
                )}
              </div>
            </TranscriptTab>
          )}

          {/* ── Docked player ─────────────────────────────────────── */}
          {/* Last child of the content column: sticky bottom pins it to
              the visible bottom of main.page (the scroller) while the
              transcript scrolls underneath; rendered OUTSIDE the tab
              panels so it survives Summary|Transcript switches; in-flow,
              so the last paragraphs always clear it at full scroll. */}
          <PlayerDock
            url={playbackUrl}
            recordedAt={meeting.recorded_at}
            onTimeUpdate={(ms) => { playbackTimeRef.current = ms; }}
            playerRef={playerRef}
            following={followState.following}
            onFollowToggle={() => {
              // Re-engage: dispatch the reengage action, then immediately
              // scroll the active turn into view (if one exists).
              dispatchFollow("reengage");
              // We trigger a programmatic scroll by dispatching reengage;
              // TranscriptView's rAF loop will pick up the new followState
              // on the next frame and scroll. No direct scrollIntoView here
              // because we don't have the activeTurnIdx in scope at MeetingReview
              // level. The rAF loop in TranscriptView reads followState from
              // the prop and scrolls on the next frame when following=true.
            }}
          />
        </div>{/* end content column */}

        {/* ── Review rail ──────────────────────────────────────── */}
        <ReviewRail
          collapsed={railCollapsed}
          onToggleCollapse={() => {
            const next = !railCollapsed;
            setRailCollapsed(next);
            writeRailCollapsed(next);
          }}
          mobileOpen={mobileRailOpen}
          onMobileClose={() => setMobileRailOpen(false)}
          reviewItems={reviewItems}
          recordedAt={meeting.recorded_at}
          isLocked={isLocked}
          meetingStatus={meeting.status}
          meetingId={meeting.id}
          workspaceId={meeting.workspace_id}
          hasAnnotationRun={
            Boolean(transcript?.attribution_synopsis) ||
            (transcript?.span_attributions?.length ?? 0) > 0
          }
          flags={flags}
          reviewPanelRef={reviewPanelRef}
          onSeek={seekTo}
          getOriginalText={getOriginalText}
          onEditSpeaker={(ms) => {
            seekTo(ms);
            setEditRequest({ ms, nonce: Date.now() });
          }}
          onActed={refreshAll}
          onReAnnotate={() => {
            setExtraContext("");
            setReannotateDialogOpen(true);
          }}
          canReAnnotate={canReAnnotate(meeting.status)}
          reannotating={reannotating}
          displayAttendees={displayAttendees}
          displayTerms={displayTerms}
          meetingProjectId={meeting.project_id ?? null}
          onEditAttendee={handleEditAttendeeDisplay}
          onRemoveAttendee={handleRemoveAttendeeDisplay}
          onEditTerm={handleEditTermDisplay}
          onRemoveTerm={handleRemoveTermDisplay}
          onToast={(msg, undoFn) => setCardToast({ message: msg, undoFn })}
          onReinsertAttendee={handleReinsertAttendee}
          onReinsertTerm={handleReinsertTerm}
          prose={meeting.prose ?? ""}
          context={meeting.context}
        />
      </div>{/* end studio body */}

      {/* ── Re-annotate dialog ───────────────────────────────────── */}
      <dialog
        ref={reannotateDialogRef}
        onClose={() => setReannotateDialogOpen(false)}
        onClick={(e) => {
          if (e.target === e.currentTarget) setReannotateDialogOpen(false);
        }}
        style={{
          border: "1px solid var(--border, #374151)",
          borderRadius: 8,
          background: "var(--surface, #181b24)",
          color: "var(--text, #f9fafb)",
          padding: 20,
          minWidth: 480,
          maxWidth: "min(640px, 90vw)",
        }}
      >
        <form
          method="dialog"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!id) return;
            const prevStatus = meeting.status;
            setMeeting({ ...meeting, status: "annotating" });
            setReannotating(true);
            setError(null);
            try {
              const trimmed = extraContext.trim();
              await reAnnotateMeeting(id, trimmed.length > 0 ? trimmed : null);
              setReannotateDialogOpen(false);
              setExtraContext("");
            } catch (err) {
              setMeeting({ ...meeting, status: prevStatus });
              setError(err instanceof Error ? err.message : String(err));
            } finally {
              setReannotating(false);
            }
          }}
        >
          <h3 style={{ marginTop: 0 }}>{t("meeting.updateAnalysis")}</h3>
          <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
            Re-runs the full analysis on the existing transcript — who said what
            (with an independent second check), suspicious wording, names &amp;
            terms, and a fresh summary. The audio and the transcript text aren't
            touched, and your own fixes survive. Cost: ~$0.25–0.50.
          </p>
          <label
            htmlFor="extra-context"
            style={{ display: "block", marginTop: 12, marginBottom: 4, fontSize: 13 }}
          >
            Anything the analysis should know? (optional)
          </label>
          <textarea
            id="extra-context"
            dir="auto"
            rows={4}
            value={extraContext}
            onChange={(e) => setExtraContext(e.target.value)}
            placeholder={
              "e.g. 'the second speaker is the woman' or " +
              "'יואב is the one talking about Fox News'"
            }
            disabled={reannotating}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: 8,
              background: "var(--surface-2, #21252f)",
              color: "var(--text, #f9fafb)",
              border: "1px solid var(--border, #374151)",
              borderRadius: 6,
              fontFamily: "inherit",
              fontSize: 14,
              resize: "vertical",
            }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <button
              type="button"
              className="secondary"
              onClick={() => setReannotateDialogOpen(false)}
              disabled={reannotating}
            >
              Cancel
            </button>
            <button type="submit" disabled={reannotating}>
              {reannotating ? t("journey.updating") : t("meeting.updateAnalysis")}
            </button>
          </div>
        </form>
      </dialog>

      {reworkRange && (
        <ReworkDialog open startMs={reworkRange.startMs} endMs={reworkRange.endMs}
          paragraphCount={reworkRange.paragraphs} recordedAt={meeting.recorded_at}
          busy={reworkBusy} onCancel={() => setReworkRange(null)}
          onConfirm={(hint) => void fireRework(hint)} />
      )}

      {/* ── Integrity Start-over modal (M2 broken verdict) ──────────── */}
      <Modal open={startOverOpen} onClose={() => setStartOverOpen(false)}>
        <div style={{ padding: 4 }}>
          <h3 style={{ marginTop: 0 }}>{t("journey.integrityHeading")}</h3>
          {transcript?.integrity?.reason && (
            <p dir="auto" style={{ fontSize: 13 }}>{transcript.integrity.reason}</p>
          )}
          <p className="muted" style={{ fontSize: 12 }}>{t("journey.integrityStartOverLead")}</p>
          <DangerZone
            meetingId={meeting.id}
            workspaceId={meeting.workspace_id}
            meetingStatus={meeting.status}
            isLocked={isLocked}
            onResubmitStart={() => {
              setStartOverOpen(false);
              const prevStatus = meeting.status;
              setMeeting({ ...meeting, status: "uploaded" });
              return prevStatus;
            }}
            onResubmitError={(prevStatus, e) => {
              setMeeting({ ...meeting, status: prevStatus });
              setError(e instanceof Error ? e.message : String(e));
            }}
          />
        </div>
      </Modal>

      {/* ── Toasts ──────────────────────────────────────────────── */}
      {promotionToast && (
        <Toast message={promotionToast} onDismiss={() => setPromotionToast(null)} />
      )}
      {cardToast && (
        <Toast
          message={cardToast.message}
          onDismiss={() => setCardToast(null)}
          action={cardToast.undoFn ? { label: "Undo", onClick: cardToast.undoFn } : undefined}
        />
      )}
    </>
  );
}

function TranscriptView({
  transcript,
  recordedAt,
  isLocked,
  onSeek,
  meetingId,
  workspaceId,
  projectId,
  playbackTimeRef,
  corrections,
  editRequest,
  paragraphSplits,
  bookmarksAtMs,
  flags,
  onFocusFlag,
  onInlineEditOpen,
  onError,
  onForceRefresh,
  viewingSource = false,
  reworkSelecting,
  onReworkRangeChosen,
  onReworkCancel,
  following = true,
  programmaticScrollRef,
  onFollowDisengage: _onFollowDisengage,
}: {
  transcript: TranscriptRow;
  recordedAt: string | null;
  isLocked: boolean;
  onSeek: (ms: number) => void;
  meetingId: string;
  workspaceId: string;
  // Meeting's current project assignment. Passed through to the
  // SpeakerOverride popover so "Add to attendee registry" writes
  // into the right project (or is hidden when unassigned).
  projectId: string | null;
  playbackTimeRef: MutableRefObject<number>;
  corrections: Correction[];
  // Item 2: ReviewPanel's "edit" affordance for Claude speaker proposals.
  // Nonce-keyed so repeat clicks on the same row re-fire the effect.
  editRequest: { ms: number; nonce: number } | null;
  // Migration 0015: user-created intra-turn paragraph splits.
  paragraphSplits: ParagraphSplit[];
  // Migration 0022: multi-bookmark per meeting. Each turn whose range
  // [start_ms, end_ms) contains any of these positions gets a
  // .bookmark-marker element in its margin. Empty array = no bookmarks.
  // Comes from meetings.bookmarks_at_ms via Realtime subscription.
  bookmarksAtMs: number[];
  // Sentinel flags for this meeting. Used to derive hasPendingFlag per turn
  // (margin glyph). Same source array loaded by MeetingReview.loadFlags.
  flags: Flag[];
  // Called when the user clicks a flag margin marker. Parent expands the
  // ReviewPanel and scrolls to the first pending item at startMs.
  onFocusFlag?: (startMs: number) => void;
  onInlineEditOpen?: () => void;
  // Surface errors from Cancel / Save into the page-level error bar.
  onError?: (msg: string) => void;
  // Force-refresh transcript + corrections + meeting + last user event.
  // Called after every edit-mode action exit path (Save / Rejoin / Dismiss /
  // Toggle bookmark / split commit / span rename) so the visual reflects the
  // DB state without waiting for Realtime delivery. Realtime is unreliable in
  // practice (the 2026-05-21 incident); the explicit refresh is the canonical
  // post-action update. Safe to double-trigger with Realtime because per-render
  // cost is low after the 2026-05-29 perf optimizations (TurnRow React.memo +
  // tokenDir cache + stable RawTokens keys + stabilized tokens ref).
  onForceRefresh?: () => Promise<void>;
  // "Hide my edits" mode: when true, the render bypasses ALL overlays --
  // no corrections, no span attributions, no paragraph splits. The user
  // sees the raw Soniox tokens with their original speaker labels.
  // Follow toggle: controls whether the rAF loop calls scrollIntoView.
  following?: boolean;
  // Ref used to flag-guard programmatic scrollIntoView calls so the
  // parent's scroll listener doesn't misread them as manual user scrolls.
  programmaticScrollRef?: MutableRefObject<number>;
  // Called by TranscriptView when it wants to disengage following (unused
  // currently — disengage happens at parent scroll-listener level). Kept
  // for future use / symmetry.
  onFollowDisengage?: () => void;
  // Toggled by the sticky-header button OR by holding Alt (peek).
  viewingSource?: boolean;
  // Rework selection mode: parent flips this on from the strip link or the
  // transcript-card button; TranscriptView owns the two tap indices.
  reworkSelecting?: boolean;
  onReworkRangeChosen?: (startMs: number, endMs: number, paragraphCount: number) => void;
  onReworkCancel?: () => void;
}) {
  // Stable token array: only produces a new reference when the payload
  // object itself changes (i.e. a new transcript was fetched from DB).
  // This prevents autoDocumentDir's useMemo from re-running on every
  // parent re-render that passes the same transcript prop by reference.
  const tokens = useMemo(
    () => transcript.payload?.transcript?.tokens ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [transcript.payload],
  );

  // NOTE (2026-06-12 live-bug fix): there is deliberately NO
  // `wordSync.resetAll()` effect here. React runs child effects before
  // parent effects, so a reset at TranscriptView level fires AFTER all
  // RawTokens callback refs have registered their spans — wiping the
  // entire registry and silencing the karaoke layer (the bug found in
  // live verification: 1718 spans in the DOM, zero .is-playing-word).
  // Cleanup is handled span-by-span: each callback ref deregisters on
  // its null call when a span unmounts (transcript reload, edit mode).

  const spanAttributions = transcript.span_attributions ?? [];
  // editingTurnIdx survives Realtime re-renders of the parent because state
  // is per-component instance. When the user saves, SpeakerOverride calls
  // onClose -> we drop back to SpeakerLabel which picks up the freshly-
  // pushed speaker_map entry.
  const [editingTurnIdx, setEditingTurnIdx] = useState<number | null>(null);
  // editingMode: set when the user clicks the ✎ button on a turn.
  // Drives the .is-editing visual transformation + floating toolbar.
  // sessionId is generated at entry for event tagging (Agents 5+6).
  // entryTimestamp is captured at entry for Agent 5's Cancel revert logic.
  const [editingMode, setEditingMode] = useState<{
    turnIdx: number;
    sessionId: string;
    entryTimestamp: string;
  } | null>(null);
  // splitMode: set when the user clicks ✂ on a turn in view mode.
  // Renders the paragraph with clickable per-token spans and a
  // "click a word to split" instruction toolbar. Only one paragraph
  // can be in split-mode at a time (mutual exclusion with editingMode).
  const [splitMode, setSplitMode] = useState<{ turnIdx: number } | null>(null);
  // splitPopoverOpen: true when the user has clicked a token in split-mode
  // and the SpeakerOverride popover is open inline below the paragraph.
  const [splitPopoverOpen, setSplitPopoverOpen] = useState(false);
  // splitTokenStartMs / splitTokenEndMs: the token the user clicked in
  // split-mode. The SpeakerOverride row gets turnStartMs = splitTokenStartMs.
  const [splitTokenStartMs, setSplitTokenStartMs] = useState<number | null>(null);
  const [splitTokenEndMs, setSplitTokenEndMs] = useState<number | null>(null);

  // Rework range selection: tap-nearest-endpoint model (spec §5 + UX refinement).
  // Single state object keeps both endpoints atomic — no dual-atom fragility.
  const [selection, setSelection] = useState<{ start: number; end: number } | null>(null);
  // Reset when reworkSelecting toggles, and on meeting id change (guarded by
  // the parent useEffect that already clears reworkSelecting on id change).
  useEffect(() => { setSelection(null); }, [reworkSelecting]);

  const handleSelectTap = useCallback((idx: number) => {
    setSelection((prev) => {
      if (prev === null) {
        // No selection yet: first tap creates a single-paragraph range.
        return { start: idx, end: idx };
      }
      // Range exists: move the NEARER endpoint.
      if (idx < prev.start) return { start: idx, end: prev.end };
      if (idx > prev.end) return { start: prev.start, end: idx };
      // Inside the range: shrink the nearer end (tie → move end).
      const distToStart = idx - prev.start;
      const distToEnd = prev.end - idx;
      if (distToStart < distToEnd) return { start: idx, end: prev.end };
      return { start: prev.start, end: idx };
    });
  }, []);

  const handleClearSelection = useCallback(() => setSelection(null), []);

  const selLo = selection?.start ?? null;
  const selHi = selection?.end ?? null;

  // activeTurnIdx is the one transcript-related state that re-renders on
  // playback. We re-render at most once per animation frame, not per
  // audioprocess tick (~20x/sec) -- see rAF effect below.
  const [activeTurnIdx, setActiveTurnIdx] = useState<number | null>(null);
  // Document-level text direction.
  //   - autoDir: computed from token dominance via documentDir()
  //   - documentDirOverride: user's explicit override (null = use auto)
  //   - resolvedDocumentDir: the dir actually applied to the .transcript
  // The override is per-session (not persisted) -- the next time the
  // meeting opens, the system re-auto-detects. Persistence can be added
  // later via a meetings column if the user wants it sticky.
  const [documentDirOverride, setDocumentDirOverride] = useState<
    "rtl" | "ltr" | null
  >(null);
  const autoDocumentDir = useMemo(() => documentDir(tokens), [tokens]);
  const resolvedDocumentDir = documentDirOverride ?? autoDocumentDir;
  // Reset-to-original confirmation dialog state. The dialog is a native
  // <dialog> element (same pattern as the Re-annotate dialog). When open,
  // we capture the turn index so the dialog knows which turn to reset.
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetDialogTurnIdx, setResetDialogTurnIdx] = useState<number | null>(null);
  const resetDialogRef = useRef<HTMLDialogElement | null>(null);
  // Resetting state for in-flight async reset (disables the Confirm button).
  const [resetting, setResetting] = useState(false);
  // Imperatively drive the native reset confirmation <dialog>.
  // Browser handles focus trap + Esc-to-dismiss; we sync state when
  // the user dismisses via Esc or backdrop click (onClose handler).
  useEffect(() => {
    const dlg = resetDialogRef.current;
    if (!dlg) return;
    if (resetDialogOpen && !dlg.open) dlg.showModal();
    if (!resetDialogOpen && dlg.open) dlg.close();
  }, [resetDialogOpen]);

  // Ref-mirror for editingMode so the document-level listeners (click-
  // outside, Escape) always see the latest value without being torn
  // down and re-added on every editingMode change.
  const editingModeRef = useRef(editingMode);
  useEffect(() => {
    editingModeRef.current = editingMode;
  }, [editingMode]);

  // WYSIWYG contenteditable ref (Task 1.8). The browser owns the DOM
  // inside the contenteditable while editing -- React MUST NOT re-render
  // its children, or the cursor jumps + edits get clobbered. We seed the
  // textContent once on edit-mode entry via the effect below; the Save
  // handler (wired in Task 1.9) reads textContent back via this ref.
  const editableRef = useRef<HTMLDivElement | null>(null);

  // Paste handler: insert ONLY plain text at the caret. Strips any rich
  // formatting (HTML, styles) the source might have copied. Manual range
  // insertion is used because document.execCommand("insertText", ...) is
  // deprecated and unreliable across browsers.
  const handleEditPaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault();
      const text = e.clipboardData.getData("text/plain");
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(document.createTextNode(text));
      // Collapse to the end of the inserted text so the caret sits after it.
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    },
    [],
  );

  // Ref-mirror for splitPopoverOpen so Escape handling can check whether
  // an inner popover has keyboard focus (and should intercept Escape
  // itself rather than letting the outer Save listener fire).
  const splitPopoverOpenRef = useRef(splitPopoverOpen);
  useEffect(() => {
    splitPopoverOpenRef.current = splitPopoverOpen;
  }, [splitPopoverOpen]);

  // Ref-mirror for displayTurns (the fully-processed turn array including
  // paragraph splits). Used in saveEditMode to read turn_start_ms without
  // capturing a stale closure. Populated by the useMemo effect further below;
  // initialised to an empty array to satisfy TypeScript.
  type DisplayTurnRef = {
    start_ms: number;
    [k: string]: unknown;
  };
  const displayTurnsRef = useRef<DisplayTurnRef[]>([]);


  // handleSave: the WYSIWYG Save flow. Replaces the Save button's
  // call to the legacy event-log-wrapping saveEditMode for the contenteditable
  // path. Steps:
  //   1. Read the edited text from the contenteditable.
  //   2. Build the AcceptedCorrection list for this sub-turn (status !=
  //      dismissed, overlapping the sub-turn's [start_ms, end_ms)).
  //   3. buildWholeSubTurnCorrection(sonioxTokens, accepted, editedText).
  //      Returns [] if nothing changed (canonicalize-equivalent round-trip).
  //      Returns a one-element array covering the whole sub-turn otherwise.
  //   4. If zero candidates, short-circuit: no RPC, no wrapper event emitted.
  //   5. Otherwise call save_wysiwyg_edit_session RPC + refresh + exit.
  //      The single whole-sub-turn candidate's range sweeps all prior accepted
  //      corrections (including Claude/sentinel rows) via the RPC's half-open
  //      overlap predicate (prior.start_ms < cand.end_ms AND prior.end_ms > cand.start_ms).
  //
  // PINNED — no auto-retry: session_id is the RPC's primary key; a retry with
  // the same session_id would PK-collide. On error, surface via onError and
  // let the user decide whether to retry (which generates a new session_id).
  const handleSave = useCallback(async () => {
    const mode = editingModeRef.current;
    if (!mode) return;
    if (!editableRef.current) return;
    const edited = editableRef.current.textContent ?? "";
    const subTurn = displayTurnsRef.current[mode.turnIdx] as
      | { start_ms: number; end_ms: number; tokens: SonioxToken[] }
      | undefined;
    if (!subTurn) {
      // Defensive: should never happen given the ✎ button only renders
      // for valid sub-turns. Exit cleanly so the user isn't trapped.
      setEditingMode(null);
      return;
    }

    // Build the AcceptedCorrection list for this sub-turn. Filter to
    // overlapping corrections, status != dismissed. Snapshot of the
    // CURRENTLY-RESOLVED state — what's on screen — used by the empty-edit
    // short-circuit to compare against what the user actually saw on entry.
    const acceptedCorrections = corrections
      .filter((c) => c.status !== "dismissed")
      .filter((c) => c.start_ms < subTurn.end_ms && c.end_ms > subTurn.start_ms)
      .map((c) => ({
        id: c.id,
        start_ms: c.start_ms,
        end_ms: c.end_ms,
        corrected_text: c.corrected_text,
        flag_id: c.flag_id,
        pre_correction_status: c.pre_correction_status ?? null,
      }));

    const candidates = buildWholeSubTurnCorrection(subTurn.tokens, acceptedCorrections, edited);

    // Empty-edit short-circuit. No RPC call, no edit_session wrapper inserted.
    // Validation checklist #4 semantic: "zero corrections + zero meeting_events
    // rows" means NO wrapper was emitted.
    if (candidates.length === 0) {
      setEditingMode(null);
      return;
    }

    // original_text contract: the single candidate's original_text is the
    // canonicalized Soniox verbatim text for the full sub-turn range
    // (assembleVerbatim with no corrections applied). The RPC's verbatim-skip
    // check depends on this contract.
    const session_id = uuidv4();
    const started_at = mode.entryTimestamp;

    try {
      await saveWysiwygEditSession({
        meeting_id: meetingId,
        workspace_id: workspaceId,
        turn_start_ms: subTurn.start_ms,
        session_id,
        started_at,
        candidates,
      });
      // Explicit refresh after DB write: Realtime delivery is unreliable in
      // practice, so pull the canonical state. Safe to double-trigger with
      // Realtime because per-render cost is low after the perf fixes.
      try { await onForceRefresh?.(); } catch { /* best-effort */ }
      setEditingMode(null);
    } catch (err) {
      // No auto-retry (see PINNED above). Surface via the page-level
      // error bar; the contenteditable + edit mode stay open so the
      // user can retry manually (generates a fresh session_id on next
      // Save click).
      // eslint-disable-next-line no-console
      console.error("handleSave: saveWysiwygEditSession failed:", err);
      onError?.(err instanceof Error ? err.message : String(err));
    }
  // displayTurnsRef + editingModeRef + editableRef are refs; meetingId /
  // workspaceId / onError / onForceRefresh are stable props.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corrections, meetingId, workspaceId, onError, onForceRefresh]);


  // Document-level mousedown listener: click-outside-paragraph = Save
  // (WYSIWYG flow: calls handleSave, not legacy saveEditMode).
  // Fires on mousedown (before click) so we can decide whether the
  // click landed inside the editing paragraph BEFORE it reaches its
  // normal click handlers. We do NOT preventDefault or stopPropagation --
  // the click must still reach its normal target (e.g., another paragraph
  // still fires seek).
  //
  // The listener is installed only while editingMode is set, and torn
  // down when editingMode clears.
  useEffect(() => {
    if (!editingMode) return;
    const turnIdx = editingMode.turnIdx;

    function handleMouseDown(e: MouseEvent) {
      // If no editing mode, bail (shouldn't happen given the effect guard,
      // but be defensive).
      if (!editingModeRef.current) return;
      // Check if the click landed inside the editing paragraph's DOM.
      const turnNode = turnNodeRefs.current[turnIdx];
      if (turnNode && turnNode.contains(e.target as Node)) return;
      // Click is outside -- WYSIWYG Save path (not legacy saveEditMode).
      void handleSave();
    }

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [editingMode?.turnIdx, handleSave]); // eslint-disable-line react-hooks/exhaustive-deps

  // Document-level keydown listener: Escape = Save (WYSIWYG flow).
  // Exception: if an inner popover (SpeakerOverride or CorrectionPopover)
  // is open AND has focus inside it, let their own Escape handlers fire
  // first. We detect this by checking if splitPopoverOpenRef is true OR
  // if document.activeElement is inside a popover. If so, skip.
  useEffect(() => {
    if (!editingMode) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (!editingModeRef.current) return;

      // If the split popover is open, let its own Escape handler fire.
      if (splitPopoverOpenRef.current) return;

      // If the active element is inside any popover-like element, let
      // its own handler take priority.
      const active = document.activeElement;
      if (active && active.closest(".speaker-override, .correction-popover, dialog")) return;

      // Escape -> WYSIWYG Save (not legacy saveEditMode). If no changes
      // were made, handleSave short-circuits via the candidates.length === 0
      // check and exits cleanly without writing to DB.
      e.stopPropagation();
      void handleSave();
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [editingMode, handleSave]);

  // One DOM node ref per turn so click-to-seek can scrollIntoView the
  // target. Sized + reset whenever the displayTurns array changes; see
  // effect after the useMemos.
  const turnNodeRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Click-vs-doubleclick disambiguation primitive. Single-click defers
  // the seek-and-play by 250ms; if a 2nd click (or doubleclick event)
  // arrives within that window, the pending seek is canceled. This is
  // the 3rd attempt at landing pause-on-edit reliably; the prior two
  // attempts (useEffect transition guard + direct synchronous call)
  // both pinned `pause()` correctly, but a browser-event-order detail
  // I'd been missing -- doubleclick fires `click` TWICE before
  // `dblclick`, and the 2nd `click` would re-fire seek-and-play AFTER
  // the popover had paused -- overrode the pause. The defer pattern
  // makes that physically impossible. See DECISIONS.md "Correction UI"
  // entry "Click-vs-dblclick deferral" amendment.
  const clickTimerRef = useRef<number | null>(null);
  useEffect(() => {
    // Cleanup on unmount so a stale timer doesn't fire seek+play
    // against a torn-down player ref (e.g., user navigates away
    // between the deferred click and its firing).
    return () => {
      if (clickTimerRef.current !== null) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
    };
  }, []);

  // Item 2: cross-component request from the ReviewPanel's "edit"
  // button on a Claude speaker proposal. Parent (MeetingReview) sets
  // editRequest = {ms, nonce} on click; we find the turn whose
  // [start_ms, end_ms) covers ms, open the inline SpeakerOverride at
  // that index, and fire pause-on-inline-edit. The nonce dependency
  // makes repeat clicks on the same row re-fire even when ms is
  // unchanged.
  useEffect(() => {
    if (!editRequest) return;
    if (isLocked) return;
    const ms = editRequest.ms;
    const idx = turnsRef.current.findIndex(
      (t) => ms >= t.start_ms && ms < t.end_ms,
    );
    if (idx < 0) return;
    setEditingTurnIdx(idx);
    onInlineEditOpen?.();
    if (programmaticScrollRef) programmaticScrollRef.current = Date.now();
    turnNodeRefs.current[idx]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editRequest?.nonce]);

  // Group consecutive tokens by speaker into "turns." Memoized so the
  // rAF loop binary-searches a stable array; turns rebuild only when the
  // transcript payload itself changes (post-transcribe / post-re-annotate).
  // end_ms is precomputed once here so the binary search doesn't walk the
  // tokens array on every frame. start_ms remains the seek target.
  //
  // tokenStartIdx is the index into the original `tokens` array where
  // the turn's first token lives -- needed for the click-on-token
  // correction path (Q5 path b) so we can pre-fill original_text and
  // reverse-map the global token index from the per-turn index.
  const turns = useMemo(() => {
    const out: {
      speaker: string | null;
      start_ms: number;
      end_ms: number;
      tokens: SonioxToken[];
      tokenStartIdx: number;
    }[] = [];
    tokens.forEach((t, i) => {
      const last = out[out.length - 1];
      if (!last || last.speaker !== (t.speaker ?? null)) {
        out.push({
          speaker: t.speaker ?? null,
          start_ms: t.start_ms,
          end_ms: t.end_ms,
          tokens: [t],
          tokenStartIdx: i,
        });
      } else {
        last.tokens.push(t);
        last.end_ms = t.end_ms;
      }
    });
    return out;
  }, [tokens]);

  // Memoized: which correction (if any) covers each token, by global
  // token index. Built once per (tokens, corrections) change so render-
  // time cascade is O(1) per token. resolveCorrectionForToken handles
  // the "most-recent-overlap wins" rule for multi-correction overlap.
  // When viewingSource is true, the map is empty -- no corrections are
  // overlaid (user wants to see the raw Soniox tokens).
  const correctionByTokenIdx = useMemo(() => {
    const out = new Map<number, Correction>();
    if (viewingSource) return out;
    tokens.forEach((t, i) => {
      const c = resolveCorrectionForToken(corrections, t.start_ms, t.end_ms);
      if (c) out.set(i, c);
    });
    return out;
  }, [tokens, corrections, viewingSource]);

  // Apply paragraph splits and mid-turn span_attributions to the turns array.
  //
  // Two split sources, merged and sorted:
  //   1. paragraphSplits (legacy Slice 2 C3/C4 mechanism, dormant but still
  //      readable): each entry divides the turn containing split_at_ms.
  //      Preserved for audit; new splits are NOT written here (see below).
  //   2. span_attributions whose start_ms > turn.start_ms AND
  //      start_ms < turn.end_ms: a mid-turn span attribution creates a
  //      visual paragraph break at that position. The second half renders
  //      with the attribution's name as its speaker label. This is the
  //      new primary split mechanism (plan Step 5d).
  //
  // splitSource discriminates the two origins so the rejoin path (Agent 7)
  // can target the correct DB record.
  //
  // When both arrays are empty this is a zero-cost pass-through.
  type DisplayTurn = (typeof turns)[0] & {
    splitId: string | null;
    splitName: string | null;
    splitSource: "paragraph_split" | "span_attribution" | null;
  };
  const displayTurns = useMemo<DisplayTurn[]>(() => {
    // viewingSource: skip ALL split processing. Render the raw
    // Soniox-grouped turns with no paragraph breaks beyond what Soniox
    // itself produced via speaker-change boundaries.
    if (viewingSource) {
      return turns.map((t) => ({
        ...t,
        splitId: null,
        splitName: null,
        splitSource: null,
      }));
    }
    // Unify both split sources into a common shape for sorting + processing.
    type SplitPoint = {
      split_at_ms: number;
      id: string;
      name: string | null;
      source: "paragraph_split" | "span_attribution";
    };

    const result: DisplayTurn[] = [];

    for (const turn of turns) {
      // Collect paragraph_split entries for this turn.
      const legacySplits: SplitPoint[] = paragraphSplits
        .filter((s) => s.split_at_ms > turn.start_ms && s.split_at_ms <= turn.end_ms)
        .map((s) => ({
          split_at_ms: s.split_at_ms,
          id: s.id,
          name: s.name,
          source: "paragraph_split" as const,
        }));

      // Collect mid-turn span_attribution entries for this turn.
      // "Mid-turn" = start_ms strictly between the turn's start and end.
      // Equal to turn.start_ms = full-turn attribution (doesn't split).
      // Equal to or >= turn.end_ms = belongs to a different turn.
      const spanSplits: SplitPoint[] = spanAttributions
        .filter(
          (a) =>
            a.start_ms > turn.start_ms &&
            a.start_ms < turn.end_ms &&
            // Same-label filter: mirrors resolveSpanAttribution's rule.
            // A mid-turn span for a different raw label shouldn't split
            // this turn's paragraph; it only applies to the label it was
            // saved against.
            (!a.raw_label_at_creation || a.raw_label_at_creation === (turn.speaker ?? "")) &&
            a.status !== "dismissed",
        )
        .map((a) => ({
          split_at_ms: a.start_ms,
          id: a.id,
          name: a.name,
          source: "span_attribution" as const,
        }));

      // Merge + sort by split_at_ms.
      const allSplits = [...legacySplits, ...spanSplits].sort(
        (a, b) => a.split_at_ms - b.split_at_ms,
      );

      if (allSplits.length === 0) {
        result.push({ ...turn, splitId: null, splitName: null, splitSource: null });
        continue;
      }

      let remaining = turn.tokens;
      let remainingStartIdx = turn.tokenStartIdx;
      let currentSplitId: string | null = null;
      let currentSplitName: string | null = null;
      let currentSplitSource: "paragraph_split" | "span_attribution" | null = null;

      for (const split of allSplits) {
        const localIdx = remaining.findIndex((t) => t.start_ms >= split.split_at_ms);
        if (localIdx <= 0) {
          // Split point before any remaining tokens — absorb into next segment.
          currentSplitId = split.id;
          currentSplitName = split.name;
          currentSplitSource = split.source;
          continue;
        }
        const firstHalf = remaining.slice(0, localIdx);
        result.push({
          speaker: turn.speaker,
          start_ms: firstHalf[0].start_ms,
          end_ms: firstHalf[firstHalf.length - 1].end_ms,
          tokens: firstHalf,
          tokenStartIdx: remainingStartIdx,
          splitId: currentSplitId,
          splitName: currentSplitName,
          splitSource: currentSplitSource,
        });
        remaining = remaining.slice(localIdx);
        remainingStartIdx += localIdx;
        currentSplitId = split.id;
        currentSplitName = split.name;
        currentSplitSource = split.source;
      }
      result.push({
        speaker: turn.speaker,
        start_ms: remaining[0].start_ms,
        end_ms: remaining[remaining.length - 1].end_ms,
        tokens: remaining,
        tokenStartIdx: remainingStartIdx,
        splitId: currentSplitId,
        splitName: currentSplitName,
        splitSource: currentSplitSource,
      });
    }
    return result;
  }, [turns, paragraphSplits, spanAttributions, viewingSource]);

  // Stable per-speaker hue assignment for the studio transcript visuals:
  // resolved display name → first-appearance index % 8 → var(--speaker-N).
  // Keyed by NAME (not raw Soniox label) so a renamed speaker keeps one hue
  // across all 5 resolution tiers (span / label / split). Recomputes only
  // when something that can change a displayed name changes; identity is
  // stable otherwise, so TurnRow's React.memo gate keeps working.
  const speakerHues = useMemo(() => {
    const hues = new Map<string, number>();
    for (const t of displayTurns) {
      let name: string | null = null;
      if (t.splitId != null) {
        name = t.splitName;
      } else if (t.speaker != null) {
        name = resolveTurnSpeaker(
          t.speaker,
          spanAttributions,
          transcript.speaker_map,
          transcript.attribution_label_map ?? {},
          t.start_ms,
        ).name;
      }
      if (!name || hues.has(name)) continue;
      hues.set(name, (hues.size % 8) + 1);
    }
    return hues;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayTurns, spanAttributions, transcript.speaker_map, transcript.attribution_label_map]);

  // Resize the per-turn DOM-node ref array when displayTurns changes,
  // so stale refs from previous payloads can't survive into re-renders.
  useEffect(() => {
    turnNodeRefs.current.length = displayTurns.length;
  }, [displayTurns]);

  // Mirror of `displayTurns` for the editRequest effect (which only depends
  // on editRequest.nonce so it doesn't re-fire on every re-memoize).
  // The effect needs the latest array at fire time; ref-mirror avoids
  // closure staleness.
  const turnsRef = useRef(displayTurns);
  useEffect(() => {
    turnsRef.current = displayTurns;
    // Also keep displayTurnsRef in sync so saveEditMode can read
    // turn_start_ms from the latest processed array.
    displayTurnsRef.current = displayTurns;
  }, [displayTurns]);

  // WYSIWYG edit-mode seed (Task 1.8). When edit mode is entered for a
  // sub-turn, populate the contenteditable's textContent ONCE with the
  // assembled resolved-before string. After this, the browser owns the
  // DOM until Save / Cancel.
  //
  // Defensive gate per spec validation #3: a sub-turn with zero tokens
  // should never reach edit mode (the ✎ button is only rendered on a
  // sub-turn whose tokens array is non-empty -- displayTurns never emits
  // empty sub-turns). If it does, we bail without seeding -- the user
  // sees an empty contenteditable and can Cancel.
  useEffect(() => {
    if (!editingMode) return;
    const node = editableRef.current;
    if (!node) return;
    const subTurn = displayTurns[editingMode.turnIdx];
    if (!subTurn || subTurn.tokens.length === 0) return;
    const accepted = corrections
      .filter(
        (c) =>
          c.status !== "dismissed" &&
          c.start_ms >= subTurn.start_ms &&
          c.end_ms <= subTurn.end_ms,
      )
      .map((c) => ({
        id: c.id,
        start_ms: c.start_ms,
        end_ms: c.end_ms,
        corrected_text: c.corrected_text,
        flag_id: c.flag_id,
        pre_correction_status: c.pre_correction_status ?? null,
      }));
    const resolved = assembleResolvedBefore(subTurn.tokens, accepted);
    node.textContent = resolved;
    node.focus();
    // Place the caret at the end of the seeded text so the user can start
    // typing immediately at a sensible position.
    const sel = window.getSelection();
    if (sel) {
      const range = document.createRange();
      range.selectNodeContents(node);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
    // editingMode.turnIdx is the only relevant entry signal; resolution-
    // input changes (corrections / displayTurns) DURING editing must NOT
    // reseed the buffer (would clobber the user's in-progress edits).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingMode?.turnIdx]);

  // Mirror of the last setActiveTurnIdx value so followingRef's re-engage
  // effect can scroll to it without closing over stale state. Declared
  // BEFORE the effect that closes over it — declaration-after-use worked
  // only by hook-ordering accident and breaks under refactor.
  const lastActiveTurnIdxRef = useRef<number | null>(null);

  // followingRef: mirrors the `following` prop so the rAF loop always sees
  // the current value without being listed in the effect's dep array
  // (which would restart the entire loop on every follow-toggle).
  const followingRef = useRef(following);
  useEffect(() => {
    followingRef.current = following;
    // When re-engaging (following flips back to true), immediately scroll
    // the current active turn into view so the user doesn't wait for a
    // turn boundary crossing to re-center.
    if (following && lastActiveTurnIdxRef.current !== null) {
      if (programmaticScrollRef) programmaticScrollRef.current = Date.now();
      turnNodeRefs.current[lastActiveTurnIdxRef.current]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [following]); // eslint-disable-line react-hooks/exhaustive-deps

  // rAF loop reads playbackTimeRef and computes the active display turn via
  // binary search on start_ms (then end_ms-bounds check so silence gaps
  // between turns clear the highlight cleanly). Updates state ONLY when
  // the index actually changes.
  //
  // Word-level karaoke: wordSync.updateMs(ms) runs EVERY frame,
  // unconditionally. It does its own change detection (element identity)
  // and self-clears when ms falls in a token gap, so repeat calls within
  // the same token cost one O(log n) binary search and nothing else —
  // no React state change, no re-render, TurnRow.memo untouched.
  // (The earlier shape only called it when the turn index was UNCHANGED,
  // which together with a parent-level resetAll effect produced the
  // zero-highlight live bug fixed 2026-06-12.)
  useEffect(() => {
    if (displayTurns.length === 0) return;
    let rafId = 0;
    let lastIdx: number | null = null;
    const tick = () => {
      const ms = playbackTimeRef.current;
      // Binary search for the largest i where displayTurns[i].start_ms <= ms.
      let lo = 0;
      let hi = displayTurns.length - 1;
      let candidate = -1;
      while (lo <= hi) {
        const mid = (lo + hi) >>> 1;
        if (displayTurns[mid].start_ms <= ms) {
          candidate = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }
      // Verify ms is still within the candidate turn's [start_ms, end_ms).
      let nextIdx: number | null = null;
      if (candidate >= 0 && ms < displayTurns[candidate].end_ms) {
        nextIdx = candidate;
      }
      if (nextIdx !== lastIdx) {
        lastIdx = nextIdx;
        lastActiveTurnIdxRef.current = nextIdx;
        setActiveTurnIdx(nextIdx);
        if (nextIdx !== null && followingRef.current) {
          if (programmaticScrollRef) programmaticScrollRef.current = Date.now();
          turnNodeRefs.current[nextIdx]?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }
      // Word layer: imperative, every frame, self-gating.
      wordSync.updateMs(ms);
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafId);
      // Clear the word highlight when the transcript unmounts or reloads.
      wordSync.clearWordHighlight();
    };
  }, [displayTurns, playbackTimeRef]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pause-on-inline-edit-popover-open principle (DECISIONS.md 2026-05-13
  // "Correction UI" entry). Fired DIRECTLY from the user-action handlers
  // that open a popover (the SpeakerLabel / SpanOverrideLabel onEdit
  // callbacks below), NOT via a derived popoverOpen useEffect.
  //
  // The original implementation used a derived `popoverOpen` value +
  // `prevPopoverOpenRef`-tracked useEffect to fire on the false -> true
  // transition. That shape looked correct structurally but didn't fire
  // reliably on real user actions (user reported: "entering into edit
  // mode doesn't stop the playback it keeps going and the screen is
  // scrolling as well"). The transition guard + effect-dep chain
  // (onInlineEditOpen identity changes on every parent render) had
  // enough indirection to hide the failure mode -- root cause never
  // empirically pinned, but the structurally-simpler direct-call shape
  // is what every other side-effect in this file uses (seek+play on
  // turn click, scrollIntoView on rAF transition). Synchronous call at
  // user-action time can't suffer effect-batching or guard-resetting
  // failure modes; cheaper to reason about and impossible to miss-
  // wire silently. Wrapped in useCallback so it's stable across renders
  // (passed to TurnRow which is memoized).
  const notifyInlineEditOpen = useCallback(() => {
    onInlineEditOpen?.();
  }, [onInlineEditOpen]);

  // Inline undo for a span attribution. Optimistic-like (the DB write
  // runs synchronously; on error we surface but the panel doesn't
  // revert -- the Realtime subscription will re-deliver the full
  // span_attributions array). One-click semantics per the orchestrator's
  // Q10 ruling: no confirm dialog, just hover-affordance + click +
  // optimistic delete + revert-on-error matching the rest of the SPA.
  const undoSpanAttribution = useCallback(
    async (id: string) => {
      const entry = spanAttributions.find((e) => e.id === id);
      try {
        await removeSpanAttribution(meetingId, id);
        if (entry) {
          try {
            await logUserEvent({
              meetingId,
              workspaceId,
              eventType: "span_attribution_remove",
              payload: { id, entry },
            });
          } catch { /* best-effort */ }
        }
        // Explicit refresh after DB write: Realtime is unreliable; pull the
        // canonical state. Safe — per-render cost is low after the perf fixes.
        try { await onForceRefresh?.(); } catch { /* best-effort */ }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("undoSpanAttribution failed:", e);
      }
    },
    [meetingId, workspaceId, spanAttributions, onForceRefresh],
  );

  // Inline undo for a correction. Flips status to 'dismissed' (does
  // NOT DELETE -- corrections preserve their content for audit per the
  // status-column asymmetry pinned in DECISIONS.md "Correction UI"
  // entry).
  const dismissCorrection = useCallback(async (id: string) => {
    try {
      await setCorrectionStatus(id, "dismissed");
      try {
        await logUserEvent({
          meetingId,
          workspaceId,
          eventType: "text_correction_dismiss",
          payload: { correction_id: id },
        });
      } catch { /* best-effort */ }
      // Explicit refresh after DB write: Realtime is unreliable; pull the
      // canonical state. Safe — per-render cost is low after the perf fixes.
      try { await onForceRefresh?.(); } catch { /* best-effort */ }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("dismissCorrection failed:", e);
    }
  }, [meetingId, workspaceId, onForceRefresh]);

  // Remove a paragraph split (rejoin). Realtime delivers the updated
  // transcript and displayTurns recomputes automatically.
  const rejoinSplit = useCallback(
    async (id: string) => {
      const entry = paragraphSplits.find((s) => s.id === id);
      try {
        await removeParagraphSplit(meetingId, id);
        if (entry) {
          try {
            await logUserEvent({
              meetingId,
              workspaceId,
              eventType: "paragraph_rejoin",
              payload: { entry },
            });
          } catch { /* best-effort */ }
        }
        // Explicit refresh after DB write: Realtime is unreliable; pull the
        // canonical state. Safe — per-render cost is low after the perf fixes.
        try { await onForceRefresh?.(); } catch { /* best-effort */ }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("rejoinSplit failed:", e);
      }
    },
    [meetingId, workspaceId, paragraphSplits, onForceRefresh],
  );

  // Toggle a bookmark on the paragraph at turnStartMs. Multi-bookmark
  // model (migration 0022): meetings.bookmarks_at_ms is an int[]. Clicking
  // 🔖 adds turnStartMs to the array if absent, or removes it if present.
  // Other bookmarked paragraphs are unaffected — the user can mark as many
  // paragraphs as they want and they all persist until explicitly removed.
  //
  // The undo payload only carries at_ms (no "previous state" needed because
  // toggling membership is its own inverse).
  const toggleBookmark = useCallback(
    async (turnStartMs: number) => {
      const { next, action } = toggleBookmarkArray(bookmarksAtMs, turnStartMs);
      const { error } = await supabase
        .from("meetings")
        .update({ bookmarks_at_ms: next })
        .eq("id", meetingId);
      if (error) {
        // eslint-disable-next-line no-console
        console.error("toggleBookmark failed:", error);
        return;
      }
      try {
        if (action === "clear") {
          const clearPayload: BookmarkClearPayload = { at_ms: turnStartMs };
          await logUserEvent({
            meetingId,
            workspaceId,
            eventType: "bookmark_clear",
            payload: clearPayload,
          });
        } else {
          const setPayload: BookmarkSetPayload = { at_ms: turnStartMs };
          await logUserEvent({
            meetingId,
            workspaceId,
            eventType: "bookmark_set",
            payload: setPayload,
          });
        }
      } catch { /* best-effort */ }
      // Explicit refresh after DB write: Realtime is unreliable; pull the
      // canonical state. Safe — per-render cost is low after the perf fixes.
      try { await onForceRefresh?.(); } catch { /* best-effort */ }
    },
    [meetingId, workspaceId, bookmarksAtMs, onForceRefresh],
  );

  // executeReset: carry out the confirmed Reset to original for the turn
  // at `resetDialogTurnIdx`. Steps per plan Step 10b:
  //   1. Snapshot every overlay affecting the turn.
  //   2. Remove each.
  //   3. Emit a single paragraph_revert event.
  //   4. Close the dialog (do NOT exit edit mode — Reset is a sub-event of
  //      the current edit session; user can continue editing or press Save).
  //
  // Partial failure: on any individual removal error, log loudly + surface
  // via onError, but continue with the rest (partial state > silent bail).
  const executeReset = useCallback(async () => {
    if (resetDialogTurnIdx === null) return;
    const rawTurn = displayTurnsRef.current[resetDialogTurnIdx];
    if (!rawTurn) return;
    // Cast to a shape that includes end_ms (displayTurnsRef uses a loose type
    // to satisfy TypeScript's indexed-ref constraint; real entries always carry
    // end_ms from the DisplayTurn type in the useMemo above).
    const turn = rawTurn as { start_ms: number; end_ms: number };

    setResetting(true);
    try {
      // 1a. Snapshot corrections affecting this turn. We query live from DB
      //     to get the current status and all columns for the reversal payload.
      const { data: correctionRows, error: corrErr } = await supabase
        .from("corrections")
        .select("id, meeting_id, workspace_id, flag_id, start_ms, end_ms, original_text, corrected_text, promote_to_glossary, status, created_at, pre_correction_status")
        .eq("meeting_id", meetingId)
        .gte("start_ms", turn.start_ms)
        .lte("end_ms", turn.end_ms)
        .neq("status", "dismissed");
      if (corrErr) throw corrErr;
      const removedCorrections = (correctionRows ?? []) as import("../lib/corrections").Correction[];

      // 1b. Snapshot span attributions affecting this turn (from in-memory array).
      const removedSpanAttributions = spanAttributions.filter(
        (a) =>
          a.start_ms >= turn.start_ms &&
          a.start_ms < turn.end_ms &&
          a.status !== "dismissed",
      );

      // 1c. Snapshot paragraph splits affecting this turn (from paragraphSplits prop).
      const removedParagraphSplits = paragraphSplits.filter(
        (s) => s.split_at_ms > turn.start_ms && s.split_at_ms <= turn.end_ms,
      );

      // 1d. Snapshot every bookmark position that falls within this turn's
      // range (multi-bookmark model — there may be more than one).
      const removedBookmarksAtMs = bookmarksAtMs.filter(
        (ms) => ms >= turn.start_ms && ms < turn.end_ms,
      );

      // 2. Execute removals. Continue past individual errors.
      for (const c of removedCorrections) {
        try {
          await setCorrectionStatus(c.id, "dismissed");
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(`executeReset: failed to dismiss correction ${c.id}:`, e);
          onError?.(e instanceof Error ? e.message : String(e));
        }
      }
      for (const sa of removedSpanAttributions) {
        try {
          await removeSpanAttribution(meetingId, sa.id);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(`executeReset: failed to remove span attribution ${sa.id}:`, e);
          onError?.(e instanceof Error ? e.message : String(e));
        }
      }
      for (const ps of removedParagraphSplits) {
        try {
          await removeParagraphSplit(meetingId, ps.id);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(`executeReset: failed to remove paragraph split ${ps.id}:`, e);
          onError?.(e instanceof Error ? e.message : String(e));
        }
      }
      if (removedBookmarksAtMs.length > 0) {
        // Remove every in-range bookmark position from the array. Other
        // bookmarks (outside this turn's range) are preserved.
        const nextBookmarks = bookmarksAtMs.filter(
          (ms) => ms < turn.start_ms || ms >= turn.end_ms,
        );
        const { error: bErr } = await supabase
          .from("meetings")
          .update({ bookmarks_at_ms: nextBookmarks })
          .eq("id", meetingId);
        if (bErr) {
          // eslint-disable-next-line no-console
          console.error("executeReset: failed to clear bookmarks:", bErr);
          onError?.(bErr.message);
        }
      }

      // 3. Emit the paragraph_revert event with full snapshots.
      const revertPayload: ParagraphRevertPayload = {
        turn_start_ms: turn.start_ms,
        turn_end_ms: turn.end_ms,
        removed_corrections: removedCorrections,
        removed_span_attributions: removedSpanAttributions,
        removed_paragraph_splits: removedParagraphSplits,
        removed_bookmarks_at_ms: removedBookmarksAtMs,
      };
      try {
        await logUserEvent({
          meetingId,
          workspaceId,
          eventType: "paragraph_revert",
          payload: revertPayload,
        });
      } catch (evErr) {
        // eslint-disable-next-line no-console
        console.error("executeReset: failed to emit paragraph_revert event:", evErr);
        onError?.(evErr instanceof Error ? evErr.message : String(evErr));
      }

      // 4. Close the dialog. Edit mode stays open (Reset is a sub-event of
      //    the current session; user can continue editing or press Save).
      setResetDialogOpen(false);
      setResetDialogTurnIdx(null);
    } catch (outerErr) {
      // eslint-disable-next-line no-console
      console.error("executeReset: unexpected error:", outerErr);
      onError?.(outerErr instanceof Error ? outerErr.message : String(outerErr));
    } finally {
      setResetting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId, workspaceId, resetDialogTurnIdx, spanAttributions, paragraphSplits, bookmarksAtMs, onError]);

  // Stable index-taking callbacks passed to TurnRow so React.memo can
  // skip re-rendering rows whose derived booleans haven't changed.
  // These use state-setter form so they don't close over mutable values.
  const onRequestEdit = useCallback((idx: number) => {
    const sessionId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setEditingMode({ turnIdx: idx, sessionId, entryTimestamp: new Date().toISOString() });
    onInlineEditOpen?.();
  }, [onInlineEditOpen]);

  const onRequestSplit = useCallback((idx: number) => {
    setEditingMode(null);
    setSplitPopoverOpen(false);
    setSplitTokenStartMs(null);
    setSplitTokenEndMs(null);
    setSplitMode({ turnIdx: idx });
  }, []);

  const onRequestEditSpeaker = useCallback((idx: number) => {
    setEditingTurnIdx(idx);
    onInlineEditOpen?.();
  }, [onInlineEditOpen]);

  const onExitEditSpeaker = useCallback(() => setEditingTurnIdx(null), []);

  const onExitEditMode = useCallback(() => setEditingMode(null), []);

  const onExitSplit = useCallback(() => {
    setSplitMode(null);
    setSplitPopoverOpen(false);
    setSplitTokenStartMs(null);
    setSplitTokenEndMs(null);
  }, []);

  // onExitSplitAfterSave: fired by the SpeakerOverride popover's onClose after
  // a successful Save (DB write committed inside SpeakerOverride). Triggers an
  // explicit refresh so the new split renders without waiting for Realtime.
  // Cancel path uses onExitSplit (no refresh) because nothing was written.
  const onExitSplitAfterSave = useCallback(() => {
    setSplitMode(null);
    setSplitPopoverOpen(false);
    setSplitTokenStartMs(null);
    setSplitTokenEndMs(null);
    void onForceRefresh?.();
  }, [onForceRefresh]);

  const onOpenSplitPopover = useCallback((startMs: number, endMs: number) => {
    setSplitTokenStartMs(startMs);
    setSplitTokenEndMs(endMs);
    setSplitPopoverOpen(true);
  }, []);

  // onRequestReset: opens the full-overlay reset dialog for a turn.
  // The dialog + executeReset exist but lack a UI trigger (the trigger was
  // never wired in the original render). Kept as a stable callback so
  // a future "Reset to original (all overlays)" affordance can be added
  // without restructuring the component. Not currently passed to TurnRow.
  const _onRequestReset = useCallback((idx: number) => {
    setResetDialogTurnIdx(idx);
    setResetDialogOpen(true);
  }, []);
  void _onRequestReset; // acknowledge unused; will be used when trigger is added

  if (tokens.length === 0) return <p className="muted">No tokens.</p>;

  // Document-direction toggle. Plain-language label that always shows
  // BOTH the current direction and the auto-detected default so the
  // user can tell whether they're overriding the system or not. Click
  // cycles: Auto (current=auto) -> the OTHER direction -> Auto.
  // Available even while in paragraph edit mode (user explicit ask).
  const isOverridden = documentDirOverride !== null;
  const toggleLabel = (() => {
    const cur = resolvedDocumentDir === "rtl" ? "Right-to-left" : "Left-to-right";
    if (!isOverridden) return `Text direction: ${cur} (auto)`;
    return `Text direction: ${cur} (you set)`;
  })();
  const cycleDocumentDir = () => {
    if (documentDirOverride === null) {
      // Currently auto -> flip to the other direction explicitly.
      setDocumentDirOverride(autoDocumentDir === "rtl" ? "ltr" : "rtl");
    } else if (documentDirOverride === autoDocumentDir) {
      // User-set to the same as auto -> back to auto.
      setDocumentDirOverride(null);
    } else {
      // User-set to the opposite of auto -> flip back to auto.
      setDocumentDirOverride(null);
    }
  };

  return (
    <>
      <div className="transcript-direction-toolbar">
        <button
          type="button"
          className="text-button"
          onClick={cycleDocumentDir}
          title="Click to flip the transcript's reading direction. The system picks a default from the dominant language; you can override it."
        >
          {toggleLabel}
        </button>
      </div>
      {/* Prominent selection-mode header — shown above the transcript while
          the user is choosing a range. Gives the screen a clear focal point
          and explains exactly what to do. */}
      {reworkSelecting && (
        <div
          data-testid="rework-selecting-header"
          style={{
            padding: "14px 0 10px",
            borderBottom: "1px solid var(--accent-soft-2, var(--border))",
            marginBottom: 8,
          }}
        >
          <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: "var(--accent)" }}>
            {t("rework.selectingHeader")}
          </p>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--text)" }}>
            {t("rework.selectingSub")}
          </p>
        </div>
      )}
      {/* rework-readout rendered as bottom dock — see below, after <div className="transcript"> */}
      <div className="transcript" dir={resolvedDocumentDir}>
      {displayTurns.map((turn, i) => {
        // Derive boolean flags for TurnRow's memo equality check.
        // Unchanged turns (same booleans + same turn object) skip re-rendering.
        const isActive = activeTurnIdx === i;
        const isEditing = editingMode?.turnIdx === i;
        const isInSplitMode = splitMode?.turnIdx === i;
        const isEditingSpeaker = editingTurnIdx === i;
        const anyOtherTurnEditing = editingMode !== null && editingMode.turnIdx !== i;
        const anyOtherSplitting = splitMode !== null && splitMode.turnIdx !== i;
        // Multi-bookmark: this turn shows a 🔖 marker if ANY bookmarked
        // position falls inside its [start_ms, end_ms) range. Multiple
        // paragraphs can be bookmarked simultaneously.
        const isBookmarked = bookmarksAtMs.some(
          (ms) => ms >= turn.start_ms && ms < turn.end_ms,
        );
        // Flag margin marker: true when any PENDING flag overlaps this turn's
        // [start_ms, end_ms) range. Derived here (parent map) and passed as a
        // boolean prop so React.memo skips rows whose flag status didn't change.
        // When viewingSource is true we suppress it (same convention as
        // corrections and bookmarks in that mode — system overlays hidden).
        const hasPendingFlag =
          !viewingSource &&
          hasPendingFlagForTurn(flags, turn.start_ms, turn.end_ms);
        return (
          <React.Fragment key={i}>
            <TurnRow
              turn={turn}
              turnIdx={i}
              isActive={isActive}
              isEditing={isEditing ?? false}
              isInSplitMode={isInSplitMode ?? false}
              isEditingSpeaker={isEditingSpeaker}
              anyOtherTurnEditing={anyOtherTurnEditing}
              anyOtherSplitting={anyOtherSplitting}
              splitPopoverOpen={splitPopoverOpen}
              splitTokenStartMs={splitTokenStartMs}
              splitTokenEndMs={splitTokenEndMs}
              isLocked={isLocked}
              isBookmarked={isBookmarked}
              hasPendingFlag={hasPendingFlag}
              selectMode={!!reworkSelecting}
              isSelected={!!reworkSelecting && selLo !== null && i >= (selLo ?? 0) && i <= (selHi ?? selLo ?? 0)}
              isSelStart={!!reworkSelecting && selLo !== null && i === selLo}
              isSelEnd={!!reworkSelecting && selHi !== null && i === selHi}
              onSelectTap={handleSelectTap}
              onFocusFlag={onFocusFlag}
              recordedAt={recordedAt}
              meetingId={meetingId}
              workspaceId={workspaceId}
              projectId={projectId}
              viewingSource={viewingSource}
              documentDirOverride={documentDirOverride}
              autoDocumentDir={autoDocumentDir}
              transcript={transcript}
              correctionByTokenIdx={correctionByTokenIdx}
              spanAttributions={spanAttributions}
              speakerHues={speakerHues}
              editableRef={editableRef}
              displayTurnsRef={displayTurnsRef}
              turnNodeRefs={turnNodeRefs}
              clickTimerRef={clickTimerRef}
              programmaticScrollRef={programmaticScrollRef}
              onSeek={onSeek}
              onError={onError}
              onForceRefresh={onForceRefresh}
              onRequestEdit={onRequestEdit}
              onRequestSplit={onRequestSplit}
              onRequestEditSpeaker={onRequestEditSpeaker}
              onExitEditSpeaker={onExitEditSpeaker}
              onExitEditMode={onExitEditMode}
              onExitSplit={onExitSplit}
              onExitSplitAfterSave={onExitSplitAfterSave}
              onOpenSplitPopover={onOpenSplitPopover}
              undoSpanAttribution={undoSpanAttribution}
              rejoinSplit={rejoinSplit}
              toggleBookmark={toggleBookmark}
              dismissCorrection={dismissCorrection}
              handleSave={handleSave}
              handleEditPaste={handleEditPaste}
              notifyInlineEditOpen={notifyInlineEditOpen}
            />
          </React.Fragment>
        );
      })}

      {/* Reset to original confirmation dialog. Native <dialog> element
          so the browser handles the focus trap and Esc-to-dismiss.
          The dialog computes overlay counts from the turn snapshot captured
          when the user clicked "↺ Reset to original". Counts are derived
          here (render time) so the dialog is always current even if
          Realtime delivers changes between the button click and this render.

          "Keep changes" is the safe default (secondary, gets autofocus).
          "Yes, reset" is the destructive action (warning-colored). */}
      <dialog
        ref={resetDialogRef}
        onClose={() => {
          setResetDialogOpen(false);
          setResetDialogTurnIdx(null);
        }}
        onClick={(e) => {
          // Backdrop click closes the dialog (same as the Re-annotate dialog).
          if (e.target === e.currentTarget) {
            setResetDialogOpen(false);
            setResetDialogTurnIdx(null);
          }
        }}
        style={{
          border: "1px solid var(--border, #374151)",
          borderRadius: 8,
          background: "var(--surface, #181b24)",
          color: "var(--text, #f9fafb)",
          padding: 20,
          minWidth: 380,
          maxWidth: "min(560px, 90vw)",
        }}
      >
        {/* Re-compute counts inside the dialog from live state so the
            counts match what will actually be removed when confirmed. */}
        {resetDialogTurnIdx !== null && (() => {
          const resetTurn = displayTurns[resetDialogTurnIdx];
          if (!resetTurn) return null;

          const activeCorrections = corrections.filter(
            (c) =>
              c.status !== "dismissed" &&
              c.start_ms >= resetTurn.start_ms &&
              c.end_ms <= resetTurn.end_ms,
          );
          const activeSpanAttributions = spanAttributions.filter(
            (a) =>
              a.start_ms >= resetTurn.start_ms &&
              a.start_ms < resetTurn.end_ms &&
              a.status !== "dismissed",
          );
          const activeParagraphSplits = paragraphSplits.filter(
            (s) => s.split_at_ms > resetTurn.start_ms && s.split_at_ms <= resetTurn.end_ms,
          );
          const bookmarksInTurn = bookmarksAtMs.filter(
            (ms) => ms >= resetTurn.start_ms && ms < resetTurn.end_ms,
          );

          return (
            <div>
              <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 16 }}>
                Reset this paragraph to the original transcript?
              </h3>
              <p style={{ fontSize: 13, margin: "0 0 8px", color: "var(--text, #f9fafb)" }}>
                This will remove all your changes to this paragraph:
              </p>
              <ul style={{ fontSize: 13, margin: "0 0 12px", paddingInlineStart: 20, lineHeight: 1.7 }}>
                {activeCorrections.length > 0 && (
                  <li>{activeCorrections.length} text correction{activeCorrections.length === 1 ? "" : "s"}</li>
                )}
                {activeSpanAttributions.length > 0 && (
                  <li>{activeSpanAttributions.length} speaker override{activeSpanAttributions.length === 1 ? "" : "s"}</li>
                )}
                {activeParagraphSplits.length > 0 && (
                  <li>{activeParagraphSplits.length} paragraph split{activeParagraphSplits.length === 1 ? "" : "s"}</li>
                )}
                {bookmarksInTurn.length > 0 && (
                  <li>
                    {bookmarksInTurn.length === 1
                      ? "The bookmark on this paragraph"
                      : `${bookmarksInTurn.length} bookmarks on this paragraph`}
                  </li>
                )}
              </ul>
              <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "0 0 16px" }}>
                This action can be undone via the global Undo button afterward.
              </p>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button
                  type="button"
                  className="secondary"
                  // autoFocus: "Keep changes" is the safe default.
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  disabled={resetting}
                  onClick={() => {
                    setResetDialogOpen(false);
                    setResetDialogTurnIdx(null);
                  }}
                >
                  Keep changes
                </button>
                <button
                  type="button"
                  disabled={resetting}
                  style={{
                    background: "var(--danger, #b91c1c)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "6px 16px",
                    cursor: resetting ? "not-allowed" : "pointer",
                    opacity: resetting ? 0.6 : 1,
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                  onClick={() => void executeReset()}
                >
                  {resetting ? "Resetting…" : "Yes, reset"}
                </button>
              </div>
            </div>
          );
        })()}
      </dialog>
    </div>

    {/* ── Rework selection dock — mirrors PlayerDock's bottom-sticky mechanism.
        position:sticky;bottom pins to the visible bottom of main.page (the
        real scroll container). Rendered AFTER the transcript div so it sits
        naturally above PlayerDock in DOM/stacking order.
        Visible ONLY while reworkSelecting is true.
        Two-group layout:
          Group A (inline-start): selection identity — range readout + Clear selection
          Group B (inline-end):   flow actions — Cancel + Continue */}
    {reworkSelecting && (
      <div
        className="rework-readout"
        data-testid="rework-readout"
      >
        {selLo !== null && selHi !== null && selLo < displayTurns.length && selHi < displayTurns.length ? (
          <>
            {/* Group A: selection identity (range + clear) */}
            <div className="rework-readout__selection">
              <span className="rework-readout__summary">
                {tVars(
                  selHi - selLo + 1 === 1 ? "rework.readoutSingle" : "rework.readout",
                  {
                    from: formatWallClock(recordedAt, displayTurns[selLo].start_ms),
                    to: formatWallClock(recordedAt, displayTurns[selHi].end_ms),
                    n: selHi - selLo + 1,
                    min: Math.max(1, Math.round((displayTurns[selHi].end_ms - displayTurns[selLo].start_ms) / 60000)),
                  },
                )}
              </span>
              <button
                type="button"
                className="secondary"
                onClick={handleClearSelection}
              >
                {t("rework.clear")}
              </button>
            </div>
            {/* Group B: whole-flow actions */}
            <div className="rework-readout__actions">
              <button
                type="button"
                className="secondary"
                onClick={() => onReworkCancel?.()}
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={() => onReworkRangeChosen?.(displayTurns[selLo!].start_ms, displayTurns[selHi!].end_ms, selHi! - selLo! + 1)}
              >
                {t("rework.continue")}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Empty state: prominent prompt + Cancel only */}
            <span className="rework-readout__hint">{t("rework.readoutHint")}</span>
            <div className="rework-readout__actions">
              <button type="button" className="secondary" onClick={() => onReworkCancel?.()}>
                {t("common.cancel")}
              </button>
            </div>
          </>
        )}
      </div>
    )}
    </>
  );
}

// ---------------------------------------------------------------------------
// TurnRow — memoized single-turn renderer.
//
// React.memo with a custom equality function: only the boolean derived
// props (isActive, isEditing, isInSplitMode, isEditingSpeaker) change
// frequently during interaction. A turn whose booleans are all unchanged
// skips re-rendering entirely, even when 599 sibling turns are in the
// same batch update (e.g., activeTurnIdx change, viewingSource toggle,
// correctionByTokenIdx rebuild).
//
// Load-bearing constraint (CLAUDE.md): the seed effect that populates the
// contenteditable lives in TranscriptView, not here. editableRef is passed
// as a prop so TurnRow can attach it to the contenteditable div.
// ---------------------------------------------------------------------------
interface TurnData {
  speaker: string | null;
  start_ms: number;
  end_ms: number;
  tokens: SonioxToken[];
  tokenStartIdx: number;
  splitId: string | null;
  splitName: string | null;
  splitSource: "paragraph_split" | "span_attribution" | null;
}

interface TurnRowProps {
  turn: TurnData;
  turnIdx: number;
  // Derived booleans — cheap to compute in the parent map, prevent TurnRow
  // from re-rendering when the full state objects change but NOT this turn.
  isActive: boolean;
  isEditing: boolean;
  isInSplitMode: boolean;
  isEditingSpeaker: boolean;
  // True when *another* turn is being edited/split (hides the actions cluster).
  anyOtherTurnEditing: boolean;
  anyOtherSplitting: boolean;
  // Split-popover state (only meaningful for the one turn in split-mode).
  splitPopoverOpen: boolean;
  splitTokenStartMs: number | null;
  splitTokenEndMs: number | null;
  // Standard context props.
  isLocked: boolean;
  // Derived in the parent map: true when any element of bookmarksAtMs falls
  // inside this turn's [start_ms, end_ms). Passing the boolean instead of the
  // whole array lets React.memo skip rows whose bookmark status didn't change
  // when an unrelated paragraph's bookmark is toggled.
  isBookmarked: boolean;
  // Derived in the parent map: true when any PENDING flag overlaps this turn's
  // [start_ms, end_ms). Passing the boolean (not the full array) lets
  // React.memo skip rows unaffected by flag status changes (same discipline
  // as isBookmarked above).
  hasPendingFlag: boolean;
  // Rework selection mode (spec §5): when true, a click on the row selects
  // it as a range endpoint instead of seeking/editing.
  selectMode: boolean;
  isSelected: boolean;
  isSelStart: boolean;
  isSelEnd: boolean;
  onSelectTap?: (idx: number) => void;
  // Called when the user clicks the flag margin marker. Expands the ReviewPanel
  // and scrolls to the matching remark. start_ms is the turn's start.
  onFocusFlag?: (startMs: number) => void;
  recordedAt: string | null;
  meetingId: string;
  workspaceId: string;
  // Meeting's current project assignment. Passed to SpeakerOverride so the
  // "Add to attendee registry" gesture writes into the right project
  // (or hides when the meeting is unassigned).
  projectId: string | null;
  viewingSource: boolean;
  documentDirOverride: "rtl" | "ltr" | null;
  // Auto-detected document direction (derived from all transcript tokens).
  // Passed as the fallback to turnDir so a signal-less turn (digits/punct
  // only) inherits the meeting's direction instead of hard-coding LTR.
  // Constant per meeting, so it does not trigger extra re-renders.
  autoDocumentDir: "rtl" | "ltr";
  transcript: TranscriptRow;
  correctionByTokenIdx: Map<number, Correction>;
  spanAttributions: SpanAttribution[];
  // Resolved display name → 1..8 hue index (first-appearance order, % 8).
  // Built once in TranscriptView; drives the Avatar + name tint per turn.
  speakerHues: Map<string, number>;
  // Refs passed from parent (stable object references).
  editableRef: React.MutableRefObject<HTMLDivElement | null>;
  displayTurnsRef: React.MutableRefObject<{ start_ms: number; [k: string]: unknown }[]>;
  turnNodeRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  clickTimerRef: React.MutableRefObject<number | null>;
  // Timestamp guard for the follow toggle: the click-to-seek recenter
  // scrollIntoView marks Date.now() here so the parent scroll listener
  // doesn't misread the smooth-scroll burst as a manual user scroll.
  programmaticScrollRef?: React.MutableRefObject<number>;
  // Stable callbacks.
  onSeek: (ms: number) => void;
  onError?: (msg: string) => void;
  // Force-refresh after the split-rename DB write (the only action handler
  // that lives inside TurnRow rather than at TranscriptView scope).
  onForceRefresh?: () => Promise<void>;
  onRequestEdit: (idx: number) => void;
  onRequestSplit: (idx: number) => void;
  onRequestEditSpeaker: (idx: number) => void;
  onExitEditSpeaker: () => void;
  onExitEditMode: () => void;
  onExitSplit: () => void;
  onExitSplitAfterSave: () => void;
  onOpenSplitPopover: (startMs: number, endMs: number) => void;
  undoSpanAttribution: (id: string) => void;
  rejoinSplit: (id: string) => void;
  toggleBookmark: (startMs: number) => void;
  dismissCorrection: (id: string) => void;
  handleSave: () => void;
  handleEditPaste: (e: React.ClipboardEvent<HTMLDivElement>) => void;
  notifyInlineEditOpen: () => void;
}

const TurnRow = React.memo(function TurnRow({
  turn,
  turnIdx,
  isActive,
  isEditing,
  isInSplitMode,
  isEditingSpeaker,
  anyOtherTurnEditing,
  anyOtherSplitting,
  splitPopoverOpen,
  splitTokenStartMs,
  splitTokenEndMs,
  isLocked,
  isBookmarked,
  hasPendingFlag,
  selectMode,
  isSelected,
  isSelStart,
  isSelEnd,
  onSelectTap,
  onFocusFlag,
  recordedAt,
  meetingId,
  workspaceId,
  projectId,
  viewingSource,
  documentDirOverride,
  autoDocumentDir,
  transcript,
  correctionByTokenIdx,
  spanAttributions,
  speakerHues,
  editableRef,
  displayTurnsRef,
  turnNodeRefs,
  clickTimerRef,
  programmaticScrollRef,
  onSeek,
  onError,
  onForceRefresh,
  onRequestEdit,
  onRequestSplit,
  onRequestEditSpeaker,
  onExitEditSpeaker,
  onExitEditMode,
  onExitSplit,
  onExitSplitAfterSave,
  onOpenSplitPopover,
  undoSpanAttribution,
  rejoinSplit,
  toggleBookmark,
  dismissCorrection,
  handleSave,
  handleEditPaste,
  notifyInlineEditOpen,
}: TurnRowProps) {
  const isSplitContinuation = turn.splitId != null;
  const editing =
    isEditingSpeaker &&
    turn.speaker != null &&
    !isLocked &&
    !isSplitContinuation;
  // isBookmarked is now a prop, derived in the parent map from
  // bookmarksAtMs[]. See TurnRowProps doc.

  // 5-tier speaker resolution for this turn (manual always beats auto;
  // per-turn beats whole-meeting).  See resolveTurnSpeaker in
  // lib/corrections.ts for the canonical precedence.
  const speakerResolution = !viewingSource && turn.speaker != null
    ? resolveTurnSpeaker(
        turn.speaker,
        spanAttributions,
        transcript.speaker_map,
        transcript.attribution_label_map ?? {},
        turn.start_ms,
      )
    : null;

  // spanOverride: the winning SpanAttribution entry (tiers 1 & 3), or null.
  // Used by isTurnUserModified (source === "manual" check stays correct)
  // and by SpanOverrideLabel.
  const spanOverride = speakerResolution?.spanEntry ?? null;

  const hasCorrectionInTurn = !viewingSource && turn.tokens.some((_t, ti) =>
    correctionByTokenIdx.has(turn.tokenStartIdx + ti),
  );
  const isSplitTurn = turn.splitId != null;
  // isModified: fires the .is-modified dashed border only for USER-originated
  // modifications. Claude's auto-source span_attributions are excluded.
  // See isTurnUserModified in lib/corrections.ts for the full rule.
  const isModified =
    !viewingSource && isTurnUserModified(hasCorrectionInTurn, spanOverride, isSplitTurn);

  // nextSameLabelBoundaries is only needed when the SpeakerOverride popover
  // is open for THIS turn; computing it eagerly for every turn is wasteful
  // but unavoidable without moving the transcript scan. In practice editing
  // is mutually exclusive, so only one turn pays this cost at a time.
  const nextSameLabelBoundaries: { end_ms: number; count: number; last_turn_end_ms: number }[] = [];
  if (editing && turn.speaker != null) {
    // Walk displayTurnsRef to find same-label turns ahead. displayTurnsRef
    // is always current (updated by the useEffect that mirrors displayTurns).
    const allTurns = displayTurnsRef.current as unknown as TurnData[];
    let runningCount = 1;
    for (let j = turnIdx + 1; j < allTurns.length; j++) {
      const nxt = allTurns[j];
      if (nxt.speaker !== turn.speaker) continue;
      runningCount += 1;
      nextSameLabelBoundaries.push({
        end_ms: nxt.end_ms,
        count: runningCount,
        last_turn_end_ms: nxt.end_ms,
      });
    }
  }

  const paragraphDir = documentDirOverride ?? turnDir(turn.tokens, autoDocumentDir);

  // Speaker identity visuals (studio redesign): the resolved display name
  // keys into speakerHues for a stable per-speaker hue; the Avatar shows
  // initials in that hue. Split continuations use their split name; raw /
  // unresolved labels get no avatar (nothing stable to identify).
  const displayName = isSplitContinuation
    ? turn.splitName
    : speakerResolution?.name ?? null;
  const speakerHue = displayName != null ? speakerHues.get(displayName) : undefined;

  return (
    <div
      ref={(node) => {
        turnNodeRefs.current[turnIdx] = node;
      }}
      dir={paragraphDir}
      className={[
        "turn",
        isActive ? "is-active" : "",
        isEditing ? "is-editing" : "",
        isInSplitMode ? "is-split-picking" : "",
        isBookmarked ? "is-bookmarked" : "",
        isModified ? "is-modified" : "",
        isSelected ? "turn--rework-insel" : "",
        isSelStart && isSelEnd ? "turn--rework-single" : (isSelStart ? "turn--rework-start" : (isSelEnd ? "turn--rework-end" : "")),
        selectMode ? "turn--rework-selecting" : "",
      ].filter(Boolean).join(" ")}
      onClickCapture={selectMode ? (e) => { e.preventDefault(); e.stopPropagation(); onSelectTap?.(turnIdx); } : undefined}
      onClick={(e) => {
        if (isEditing) return;
        if (isInSplitMode) return;
        if (isEditingSpeaker) return;
        const sel = window.getSelection();
        if (sel && !sel.isCollapsed && sel.toString().trim().length > 0) return;
        // Word-precise seek: read the clicked word's data-token-start-ms
        // (each raw-token span carries it) and seek there; fall back to the
        // paragraph start when the click missed a word. Captured synchronously
        // here — the synthetic event must not be read inside the timer.
        const tokenEl = (e.target as HTMLElement).closest?.("[data-token-start-ms]");
        const seekMs = resolveSeekMs(
          tokenEl?.getAttribute("data-token-start-ms") ?? null,
          turn.start_ms,
        );
        if (clickTimerRef.current !== null) {
          clearTimeout(clickTimerRef.current);
          clickTimerRef.current = null;
          return;
        }
        // Stamp the programmatic window SYNCHRONOUSLY at click time, not only
        // inside the deferred timer: trackpad inertia from a prior swipe can
        // deliver scroll events during the 250ms defer, and onSeek may itself
        // trigger a synchronous scroll — neither must disengage Following.
        if (programmaticScrollRef) programmaticScrollRef.current = Date.now();
        clickTimerRef.current = window.setTimeout(() => {
          clickTimerRef.current = null;
          // Re-stamp: the recenter below is OURS, not a user scroll.
          if (programmaticScrollRef) programmaticScrollRef.current = Date.now();
          onSeek(seekMs);
          turnNodeRefs.current[turnIdx]?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 250);
      }}
      onDoubleClick={() => {
        if (clickTimerRef.current !== null) {
          clearTimeout(clickTimerRef.current);
          clickTimerRef.current = null;
        }
      }}
      style={{ cursor: "pointer" }}
    >
      {/* Rework selection endpoint caps — rendered when this turn is the
          start and/or end of the current selection. Position: absolute at
          the inline-end top/bottom corner (logical, RTL-safe). */}
      {isSelected && isSelStart && isSelEnd && (
        <span className="turn-rework-cap turn-rework-cap--single" aria-hidden>
          {t("rework.capSingle")}
        </span>
      )}
      {isSelected && isSelStart && !isSelEnd && (
        <span className="turn-rework-cap turn-rework-cap--start" aria-hidden>
          {t("rework.capStart")}
        </span>
      )}
      {isSelected && isSelEnd && !isSelStart && (
        <span className="turn-rework-cap turn-rework-cap--end" aria-hidden>
          {t("rework.capEnd")}
        </span>
      )}
      {isBookmarked && (
        <button
          type="button"
          className="bookmark-marker"
          title="Your bookmark — click to jump here"
          aria-label="Bookmarked paragraph"
          onClick={(e) => {
            e.stopPropagation();
            onSeek(turn.start_ms);
          }}
        >
          <Icon name="bookmark" size={13} aria-hidden />
        </button>
      )}
      {hasPendingFlag && (
        <button
          type="button"
          className="flag-marker"
          title="Claude flagged this — click to review"
          aria-label="Claude has a note about this paragraph"
          onClick={(e) => {
            e.stopPropagation();
            onFocusFlag?.(turn.start_ms);
          }}
        >
          ⚑
        </button>
      )}
      {/* Start-side gutter: the paragraph timestamp lives here (CSS grid
          column 1 on .turn). Bookmark/flag markers sit in the same gutter
          via absolute positioning (see global.css). */}
      <span className="turn-gutter">
        <span className="ts">{formatWallClock(recordedAt, turn.start_ms)}</span>
      </span>
      <div className="turn-content">
      {isSplitContinuation ? (
        <SplitTurnHeader
          name={turn.splitName}
          splitId={turn.splitId!}
          hue={speakerHue}
          isLocked={isLocked}
          onRejoin={() => {
            if (turn.splitSource === "span_attribution") {
              void undoSpanAttribution(turn.splitId!);
            } else {
              void rejoinSplit(turn.splitId!);
            }
          }}
          onRename={
            turn.splitSource === "span_attribution"
              ? async (newName: string, promote: boolean) => {
                  const currentName = turn.splitName ?? "";
                  const nameChanged = newName !== currentName;
                  try {
                    if (nameChanged) {
                      await renameSpanAttribution(meetingId, turn.splitId!, newName);
                      try {
                        await logUserEvent({
                          meetingId,
                          workspaceId,
                          eventType: "span_attribution_rename",
                          payload: { id: turn.splitId, new_name: newName },
                        });
                      } catch { /* best-effort */ }
                    }
                    if (promote) {
                      const { error: insertErr } = await supabase
                        .from("attendees")
                        .insert({ workspace_id: workspaceId, display_name: newName, role: "", notes: "" });
                      if (insertErr) {
                        onError?.(`Saved name, but couldn't add to registry: ${insertErr.message}`);
                      }
                    }
                    // Explicit refresh after DB write: Realtime is unreliable;
                    // pull the canonical state. Safe — per-render cost is low
                    // after the perf fixes.
                    try { await onForceRefresh?.(); } catch { /* best-effort */ }
                  } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error("renameSpanAttribution failed:", e);
                    onError?.(e instanceof Error ? e.message : String(e));
                  }
                }
              : undefined
          }
        />
      ) : (
        <>
          <span
            className="who"
            style={speakerHue !== undefined ? { color: `var(--speaker-${speakerHue})` } : undefined}
          >
            {!editing && displayName != null && (
              <Avatar name={displayName} hueIndex={speakerHue} size={22} className="turn-avatar" />
            )}
            {turn.speaker == null ? (
              "?"
            ) : editing ? (
              <SpeakerOverride
                meetingId={meetingId}
                speakerKey={turn.speaker}
                initialName={speakerResolution?.name ?? null}
                rawLabel={resolveSpeakerEntry(transcript.speaker_map, turn.speaker)?.raw_label ?? turn.speaker}
                workspaceId={workspaceId}
                projectId={projectId}
                turnStartMs={turn.start_ms}
                turnEndMs={turn.end_ms}
                nextSameLabelBoundaries={nextSameLabelBoundaries}
                recordedAt={recordedAt}
                onClose={onExitEditSpeaker}
              />
            ) : spanOverride ? (
              // Tiers 1 (manual-span) and 3 (auto-span): a SpanAttribution
              // entry won — render the override label with undo/edit affordance.
              <SpanOverrideLabel
                spanOverride={spanOverride}
                rawLabel={resolveSpeakerEntry(transcript.speaker_map, turn.speaker)?.raw_label ?? turn.speaker}
                isLocked={isLocked}
                onEdit={() => {
                  onRequestEditSpeaker(turnIdx);
                  notifyInlineEditOpen();
                }}
                onUndo={() => undoSpanAttribution(spanOverride.id)}
              />
            ) : (
              // Tiers 2 (manual-label), 4 (auto-label), 5 (none): no
              // span entry won.  SpeakerLabel reads from speaker_map (tier 2)
              // and attribution_label_map (tier 4) in the correct priority
              // order via the pre-resolved speakerResolution result.
              <SpeakerLabel
                speaker={turn.speaker}
                map={transcript.speaker_map}
                labelMap={transcript.attribution_label_map ?? {}}
                isLocked={isLocked}
                onEdit={() => {
                  onRequestEditSpeaker(turnIdx);
                  notifyInlineEditOpen();
                }}
              />
            )}
          </span>
        </>
      )}
      {isEditing ? (
        <div
          ref={editableRef}
          contentEditable
          suppressContentEditableWarning
          spellCheck={false}
          onPaste={handleEditPaste}
          className="wysiwyg-editor"
        />
      ) : isInSplitMode ? (
        <RawTokens
          tokens={turn.tokens}
          tokenStartIdx={turn.tokenStartIdx}
          splitPickMode
          onTokenClick={(token) => {
            onOpenSplitPopover(token.start_ms, turn.end_ms);
          }}
        />
      ) : (
        <CorrectionAwareText
          tokens={turn.tokens}
          tokenStartIdx={turn.tokenStartIdx}
          correctionByTokenIdx={correctionByTokenIdx}
          isLocked={isLocked}
          onDismissCorrection={dismissCorrection}
        />
      )}
      {!isEditing && !isInSplitMode && !anyOtherTurnEditing && !anyOtherSplitting && (
        <span className="paragraph-actions">
          {!isLocked && (
            <button
              type="button"
              className="paragraph-action-btn"
              title="Edit this paragraph"
              onClick={(e) => {
                e.stopPropagation();
                onRequestEdit(turnIdx);
              }}
            >
              <Icon name="edit" size={13} aria-hidden />
            </button>
          )}
          {!isLocked && (
            <button
              type="button"
              className="paragraph-action-btn"
              title="Split this paragraph"
              onClick={(e) => {
                e.stopPropagation();
                onRequestSplit(turnIdx);
              }}
            >
              <Icon name="scissors" size={13} aria-hidden />
            </button>
          )}
          <button
            type="button"
            className={`paragraph-action-btn is-bookmark-toggle${isBookmarked ? " is-bookmarked" : ""}`}
            title={isBookmarked ? "Remove the bookmark from this paragraph" : "Bookmark this paragraph for later"}
            onClick={(e) => {
              e.stopPropagation();
              void toggleBookmark(turn.start_ms);
            }}
          >
            <Icon name="bookmark" size={13} aria-hidden />
          </button>
        </span>
      )}
      {isEditing && (
        <>
          <div className="edit-mode-toolbar" onClick={(e) => e.stopPropagation()}>
            <div className="toolbar-left">
              <button
                type="button"
                className="secondary"
                title="Clear all edits in this paragraph back to the original transcript (no DB write — press Save or Cancel to confirm)"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!editableRef.current) return;
                  const subTurn = displayTurnsRef.current[turnIdx] as unknown as
                    | { tokens: SonioxToken[] }
                    | undefined;
                  if (!subTurn) return;
                  const verbatim = subTurn.tokens.map((t) => t.text).join("");
                  editableRef.current.textContent = verbatim;
                }}
              >
                <Icon name="undo" size={13} aria-hidden /> Reset to original
              </button>
            </div>
            <div className="toolbar-right">
              <button
                type="button"
                className="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  onExitEditMode();
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  void handleSave();
                }}
              >
                Save
              </button>
            </div>
          </div>
        </>
      )}
      {isInSplitMode && (
        <div className="split-mode-toolbar" onClick={(e) => e.stopPropagation()}>
          {!splitPopoverOpen && (
            <div className="split-mode-instructions">
              Click a word where you want to split this paragraph. The split will start at that word.
            </div>
          )}
          {splitPopoverOpen && splitTokenStartMs !== null && splitTokenEndMs !== null && (
            <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
              <SpeakerOverride
                meetingId={meetingId}
                speakerKey={turn.speaker ?? ""}
                initialName={speakerResolution?.name ?? null}
                rawLabel={resolveSpeakerEntry(transcript.speaker_map, turn.speaker ?? "")?.raw_label ?? turn.speaker ?? ""}
                workspaceId={workspaceId}
                projectId={projectId}
                turnStartMs={splitTokenStartMs}
                turnEndMs={splitTokenEndMs}
                nextSameLabelBoundaries={[]}
                recordedAt={recordedAt}
                splitMode
                onClose={onExitSplitAfterSave}
              />
            </div>
          )}
          <button
            type="button"
            className="secondary"
            style={{ marginTop: 6 }}
            onClick={(e) => {
              e.stopPropagation();
              onExitSplit();
            }}
          >
            Cancel
          </button>
        </div>
      )}
      </div>{/* end turn-content */}
    </div>
  );
}, (prev, next) => {
  // Custom equality: only re-render when something affecting this turn changed.
  // The boolean flags are the main gate — when none flip, skip the render.
  if (prev.isActive !== next.isActive) return false;
  if (prev.isEditing !== next.isEditing) return false;
  if (prev.isInSplitMode !== next.isInSplitMode) return false;
  if (prev.isEditingSpeaker !== next.isEditingSpeaker) return false;
  if (prev.anyOtherTurnEditing !== next.anyOtherTurnEditing) return false;
  if (prev.anyOtherSplitting !== next.anyOtherSplitting) return false;
  // Split-popover state (only relevant for the in-split-mode turn).
  if (prev.isInSplitMode && (
    prev.splitPopoverOpen !== next.splitPopoverOpen ||
    prev.splitTokenStartMs !== next.splitTokenStartMs ||
    prev.splitTokenEndMs !== next.splitTokenEndMs
  )) return false;
  // Turn data identity (changes when displayTurns recomputes after a split/rejoin).
  if (prev.turn !== next.turn) return false;
  // Correction map identity (changes after a correction save/dismiss).
  if (prev.correctionByTokenIdx !== next.correctionByTokenIdx) return false;
  // Bookmark (affects marker visibility). Derived in parent; flipping only
  // for the affected paragraph(s) means other rows skip re-rendering when
  // one bookmark is toggled (the whole point of moving derivation up).
  if (prev.isBookmarked !== next.isBookmarked) return false;
  // Flag marker (affects amber glyph visibility). Derived in parent from
  // flags[].status; only the affected paragraph re-renders when a flag is
  // accepted/dismissed. Same discipline as isBookmarked.
  if (prev.hasPendingFlag !== next.hasPendingFlag) return false;
  if (prev.selectMode !== next.selectMode) return false;
  if (prev.isSelected !== next.isSelected) return false;
  if (prev.isSelStart !== next.isSelStart) return false;
  if (prev.isSelEnd !== next.isSelEnd) return false;
  // Lock state (affects affordances).
  if (prev.isLocked !== next.isLocked) return false;
  // viewingSource (affects correction + span-override bypass).
  if (prev.viewingSource !== next.viewingSource) return false;
  // Speaker map (affects name display).
  if (prev.transcript.speaker_map !== next.transcript.speaker_map) return false;
  // Span attributions (affects spanOverride resolve + split display).
  if (prev.spanAttributions !== next.spanAttributions) return false;
  // Speaker hue map (affects avatar + name tint). Identity changes only
  // when a displayed name can change (same inputs as the resolve above).
  if (prev.speakerHues !== next.speakerHues) return false;
  // documentDirOverride (affects paragraph direction).
  if (prev.documentDirOverride !== next.documentDirOverride) return false;
  // autoDocumentDir (fallback for signal-less turns). Constant per meeting in
  // practice, but if another turn's tokens flip the document direction on a
  // refresh, a digits/punct-only turn must re-render to inherit the new dir.
  if (prev.autoDocumentDir !== next.autoDocumentDir) return false;
  // All stable: skip re-render.
  return true;
});

// Render a turn's tokens with the correction-overlay cascade applied.
// For each token: if a correction covers it AND status='accepted', the
// correction's corrected_text renders in place (token-atomic; multiple
// tokens covered by the same correction merge into one render unit).
// Otherwise the raw verbatim renders with the existing script-aware
// per-token direction logic.
//
// Locked mode keeps the same render path; editing affordances hide
// and overlays persist as the locked-mode truth.
//
// Each raw-token span carries data-token-start-ms / data-token-end-ms
// attrs for inspection / e2e targeting. No per-token onClick lives on
// RawTokens in normal mode; click bubbles to the turn's onClick
// (seek-to-play). See DECISIONS.md "Correction UI" entry.
function CorrectionAwareText({
  tokens,
  tokenStartIdx,
  correctionByTokenIdx,
  isLocked,
  onDismissCorrection,
}: {
  tokens: SonioxToken[];
  tokenStartIdx: number;
  correctionByTokenIdx: Map<number, Correction>;
  isLocked: boolean;
  onDismissCorrection: (id: string) => void;
}) {
  // Pre-walk to build segments. A "segment" is either:
  //   - A run of raw tokens (no correction): rendered with script-aware
  //     direction grouping (same as the prior ScriptAwareText shape).
  //   - A single correction span: one or more contiguous tokens covered
  //     by the same correction id, rendered as the corrected text.
  type Seg =
    | { kind: "raw"; tokens: SonioxToken[]; globalIndices: number[] }
    | { kind: "correction"; correction: Correction };
  const segs: Seg[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const globalIdx = tokenStartIdx + i;
    const c = correctionByTokenIdx.get(globalIdx);
    if (c) {
      // If the previous seg is the same correction, skip (merged).
      const last = segs[segs.length - 1];
      if (last && last.kind === "correction" && last.correction.id === c.id) {
        continue;
      }
      segs.push({ kind: "correction", correction: c });
    } else {
      const last = segs[segs.length - 1];
      if (last && last.kind === "raw") {
        last.tokens.push(tokens[i]);
        last.globalIndices.push(globalIdx);
      } else {
        segs.push({ kind: "raw", tokens: [tokens[i]], globalIndices: [globalIdx] });
      }
    }
  }
  return (
    <>
      {segs.map((seg, i) => {
        // Render the segment itself
        const el =
          seg.kind === "correction" ? (
            <CorrectedSpan
              key={`c-${seg.correction.id}-${i}`}
              correction={seg.correction}
              isLocked={isLocked}
              onDismiss={() => onDismissCorrection(seg.correction.id)}
            />
          ) : (
            <RawTokens
              key={`r-${i}-${seg.globalIndices[0]}`}
              // Segment's OWN first global index — NOT the turn's
              // tokenStartIdx. seg.globalIndices is contiguous, so the
              // first entry + offset reproduces every token's true global
              // index. Passing the turn's start here (the old bug) made
              // segments after a correction register colliding indices
              // in wordSync, clobbering each other's spans.
              tokens={seg.tokens}
              tokenStartIdx={seg.globalIndices[0]}
            />
          );
        return (
          <React.Fragment key={i}>
            {el}
          </React.Fragment>
        );
      })}
    </>
  );
}

// Rendered correction overlay: corrected_text with a dotted underline
// (per the orchestrator's verify-driven starting point -- spell-checker
// vocabulary, accent-colored), tooltip surfacing original_text for
// inspection. Click-to-undo affordance only during review (locked mode
// hides the dismiss button -- the lock IS the user's acceptance).
function CorrectedSpan({
  correction,
  isLocked,
  onDismiss,
}: {
  correction: Correction;
  isLocked: boolean;
  onDismiss: () => void;
}) {
  // Visibility of the undo ✗ is controlled by CSS opacity on
  // .corrected-span:hover (matches the .speaker-label-edit-icon
  // pattern). Earlier shape used React `hovering` state +
  // conditional render, which produced a race condition: cursor
  // moving from the underlined text toward the button could fire
  // mouseleave -> unmount before the click landed, dropping the
  // click silently. CSS-opacity keeps the button mounted at all
  // times and clicks register reliably during hover.
  return (
    <span
      className="corrected-span"
      title={`Corrected from: ${correction.original_text ?? ""}`}
      dir="auto"
      onClick={(e) => e.stopPropagation()}
    >
      {correction.corrected_text}
      {!isLocked && (
        <button
          type="button"
          className="corrected-span-undo"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          title="Undo this correction (preserved as dismissed in audit trail)"
        >
          ✗
        </button>
      )}
    </span>
  );
}

// Raw token segment. Groups consecutive same-direction tokens into
// single bidi-isolated spans (existing script-aware direction grouping
// pattern). No per-token click handler in normal mode: single-click
// bubbles up to the turn's onClick (seek-to-play). Lock state has no
// effect on this render -- affordance gating happens in the parent turn.
//
// splitPickMode: when true, each token renders as a clickable target
// for the ✂ split-point picker. Tokens get a pointer cursor +
// hover highlight. onTokenClick fires with the clicked SonioxToken.
//
// wordSync registration: each token span registers itself with the
// module-level wordSync controller via a callback ref (register on el,
// deregister on null). Registration is imperative (no React state); the
// controller manipulates classList directly to highlight the active word
// without re-rendering any TurnRow. The controller sorts its registry
// lazily inside updateMs() (dirty flag), so no commit step is needed
// here — robust to refs re-firing on TurnRow re-renders mid-playback.
// Only non-splitPickMode spans are registered (split-mode is transient
// and doesn't need karaoke highlight).
function RawTokens({
  tokens,
  tokenStartIdx = 0,
  splitPickMode = false,
  onTokenClick,
}: {
  tokens: SonioxToken[];
  tokenStartIdx?: number;
  splitPickMode?: boolean;
  onTokenClick?: (token: SonioxToken) => void;
}) {
  type Group = {
    dir: "rtl" | "ltr";
    // Each token paired with its GLOBAL index (position in the full
    // transcript token array) — the identity key for wordSync register/
    // deregister, computed once here so the ref callbacks are O(1).
    tokens: { t: SonioxToken; globalIdx: number }[];
    // Global index of the first token in this group. Used as the stable
    // React key so React reuses group span nodes across re-renders.
    firstGlobalIdx: number;
  };
  // localIdx tracks the current token's position relative to tokenStartIdx.
  let localIdx = 0;
  const groups: Group[] = [];
  for (const t of tokens) {
    const globalIdx = tokenStartIdx + localIdx;
    const d = tokenDir(t.text);
    const last = groups[groups.length - 1];
    if (last && last.dir === d) {
      last.tokens.push({ t, globalIdx });
    } else {
      groups.push({
        dir: d,
        tokens: [{ t, globalIdx }],
        firstGlobalIdx: globalIdx,
      });
    }
    localIdx++;
  }

  // Per-group rendering: an outer <span dir> wrapper (bidi isolation +
  // cursor:text CSS via .raw-token-group) containing PER-TOKEN inner
  // <span class="raw-token"> elements. Each inner span carries its own
  // data-token-start-ms / data-token-end-ms for inspection / e2e
  // targeting of a specific token within a group.
  return (
    <>
      {groups.map((g) => (
        <React.Fragment key={g.firstGlobalIdx}>
          <span dir={g.dir} className="raw-token-group">
            {g.tokens.map(({ t, globalIdx }) => (
              <span
                key={globalIdx}
                className={splitPickMode ? "raw-token split-pick-token" : "raw-token"}
                data-token-start-ms={t.start_ms}
                data-token-end-ms={t.end_ms}
                ref={!splitPickMode ? (() => {
                  // IIFE closure: remembers WHICH element this ref instance
                  // registered, so the null call deregisters only its own
                  // element. React calls the OLD ref with null before the
                  // NEW ref with el — without the pairing, a transcript
                  // reload that reuses a globalIdx could have the old span's
                  // null-call remove the NEW span's registration.
                  let mine: HTMLSpanElement | null = null;
                  return (el: HTMLSpanElement | null) => {
                    if (el) {
                      mine = el;
                      wordSync.register({
                        globalIdx,
                        startMs: t.start_ms,
                        endMs: t.end_ms,
                        el,
                      });
                    } else {
                      wordSync.deregister(globalIdx, mine ?? undefined);
                    }
                  };
                })() : undefined}
                onClick={splitPickMode && onTokenClick ? (e) => {
                  e.stopPropagation();
                  onTokenClick(t);
                } : undefined}
              >
                {t.text}
              </span>
            ))}
          </span>
        </React.Fragment>
      ))}
    </>
  );
}

// Span-override speaker label: rendered as `Ilan ✎ [spk_2]` during
// review; pencil glyph indicates the attribution was user-overridden.
// Tooltip on the pencil surfaces created_at + the raw label at
// override time (open-the-hood audit). Click on the name opens the
// SpeakerOverride popover (same as a regular SpeakerLabel) so the user
// can edit the override. Hover affordance includes a small undo ✗ that
// removes the entry without going through the popover.
//
// Locked mode (DECISIONS.md Q8): hide both the pencil and the raw
// label, show only the override name -- the lock IS the user's
// acceptance.
function SpanOverrideLabel({
  spanOverride,
  rawLabel,
  isLocked,
  onEdit,
  onUndo,
}: {
  spanOverride: SpanAttribution;
  rawLabel: string;
  isLocked: boolean;
  onEdit: () => void;
  onUndo: () => void;
}) {
  if (isLocked) {
    return <span>{spanOverride.name}</span>;
  }
  const when = new Date(spanOverride.created_at).toLocaleString();
  // Source-aware tooltip distinguishes user-authored from Claude-
  // attributed entries per the open-the-hood visibility principle. Same
  // pencil glyph for all sources -- the source distinction is in the
  // audit surface (tooltip), not in chrome.
  //
  // UI-copy convention (2026-05-14 + 2026-05-25 auto-source fix):
  //   - "auto" = Claude attribution (new default path). These were
  //     incorrectly falling into the "by you" branch before this fix.
  //   - "L3" = Claude suggestion (legacy opt-in, review-pending
  //     semantics). Wording preserved as-is ("suggestion" signals that
  //     the entry is in the review queue).
  //   - "manual" = Span-attributed by you (user-authored ground truth).
  // Internal codenames (L3, auto) stay in code + audit logs only.
  let pencilTitle: string;
  if (spanOverride.source === "L3") {
    pencilTitle =
      `Claude suggestion ${when}${
        spanOverride.confidence != null
          ? ` (confidence ${spanOverride.confidence.toFixed(2)})`
          : ""
      } — original label spk_${spanOverride.raw_label_at_creation}${
        spanOverride.reasoning ? `\n\n${spanOverride.reasoning}` : ""
      }`;
  } else if (spanOverride.source === "auto") {
    pencilTitle =
      `Claude attribution ${when}${
        spanOverride.confidence != null
          ? ` (confidence ${spanOverride.confidence.toFixed(2)})`
          : ""
      } — original label spk_${spanOverride.raw_label_at_creation}${
        spanOverride.reasoning ? `\n\n${spanOverride.reasoning}` : ""
      }`;
  } else {
    // manual
    pencilTitle = `Assigned by you ${when} — original label spk_${spanOverride.raw_label_at_creation}`;
  }
  return (
    <span
      className="speaker-label-editable"
      title="Click to rename"
      onClick={(e) => {
        e.stopPropagation();
        onEdit();
      }}
    >
      {spanOverride.name}
      <span
        title={pencilTitle}
        style={{
          display: "inline-flex",
          alignItems: "center",
          marginInlineStart: 4,
          color: "var(--accent)",
        }}
      >
        <Icon name="edit" size={12} aria-hidden />
      </span>
      <span
        style={{
          marginLeft: 6,
          color: "var(--text-muted)",
          fontFamily: "monospace",
          fontSize: "0.85em",
        }}
      >
        [spk_{rawLabel}]
      </span>
      <button
        type="button"
        className="span-override-undo"
        onClick={(e) => {
          e.stopPropagation();
          onUndo();
        }}
        title="Remove this assignment and restore Claude's original speaker"
      >
        ✗
      </button>
    </span>
  );
}

function resolveSpeakerEntry(
  map: Record<string, SpeakerMapEntry>,
  speaker: string,
): SpeakerMapEntry | null {
  return map[speaker] ?? map[`speaker_${speaker}`] ?? null;
}

// canResubmit removed 2026-05-20 with the Resubmit DangerZone demotion;
// the same status gate now lives inside components/DangerZone.tsx so
// the action's preconditions ride with the component that owns the UX.

// canReAnnotate is imported from ./meeting/MeetingHeader (canLock was removed).
// LockBadge is now rendered inside MeetingHeader.

// SpeakerLabel renders the mapped name alongside the raw Soniox label.
//
// Migration 0007 / DECISIONS.md 2026-05-13 "Confidence threshold moves
// from storage-time to render-time decision":
//   - During review (isLocked=false), the user sees both the mapped name
//     and the raw label "[spk_N]" so they can spot mismatches between
//     Soniox's diarization and Claude's attribution. This is the
//     "open-the-hood / n8n-style visibility" principle.
//   - After lock (isLocked=true), raw labels are hidden -- the user has
//     accepted the mapping. EXCEPT when name is null (no confident
//     mapping); we keep the raw label visible so the user always sees
//     "Soniox produced a label that Claude couldn't assign" rather than
//     a silent "speaker N" downgrade.
//   - A "?" glyph appears next to low-confidence entries DURING REVIEW.
//     After lock, the "?" drops for entries with a name (the lock IS
//     the user's acceptance -- post-lock "?" would ask the reader to
//     wonder about something that needs no further action). The "?"
//     stays for name=null entries (no name was ever accepted, so the
//     uncertainty is still load-bearing).
//
// Speaker name cascade (5-tier) — SpeakerLabel handles tiers 2, 4, and 5
// (SpanOverrideLabel handles tiers 1 & 3 in the caller branch above):
//   tier 2: speaker_map[speaker] — manual "Whole meeting" rename (highest
//           priority here; written ONLY by the "Whole meeting" rename UI,
//           NOT the retired speaker_mapper.py).
//   tier 4: attribution_label_map[speaker] — Claude's label-level default.
//   tier 5: neither → "Not attributed" pill (genuine gap).
function SpeakerLabel({
  speaker,
  map,
  labelMap,
  isLocked,
  onEdit,
}: {
  speaker: string;
  map: Record<string, SpeakerMapEntry>;
  labelMap: Record<string, { name: string | null; confidence: number }>;
  isLocked: boolean;
  onEdit: () => void;
}) {
  // Speaker labels from Soniox come as "1", "2", ... or sometimes "speaker_0".
  // Try the bare key first, fall back to the prefixed form.
  const mapEntry = map[speaker] ?? map[`speaker_${speaker}`];

  // Auto label default (tier 4): attribution_label_map default for this label.
  // Try the bare key first (matches the spk value from Soniox), fall back
  // to the prefixed form in case the label was stored differently.
  const labelMapEntry = labelMap[speaker] ?? labelMap[`speaker_${speaker}`];

  // 5-tier cascade precedence (when SpeakerLabel is reached, tiers 1 & 3
  // — span entries — have already been handled by SpanOverrideLabel):
  //   tier 2: speaker_map (manual "Whole meeting" rename) wins over
  //   tier 4: attribution_label_map (Claude's label-level default).
  // Use mapEntry first; fall back to labelMapEntry only when no manual
  // whole-meeting rename exists.
  const entry: SpeakerMapEntry | { name: string | null; confidence: number; raw_label?: string } | undefined =
    mapEntry ?? labelMapEntry;

  let content: JSX.Element;
  if (!entry) {
    // No attribution output for this label — neither a span_attribution
    // nor a speaker_map/label_map entry covers this turn. This is a
    // genuine gap: either the meeting was annotated before the new
    // attribution pipeline, or Claude's output didn't cover this label
    // (an old run before the coverage check was added).
    //
    // Show a visible "not attributed" pill so the user sees the gap as
    // an explicit signal, not a silent raw label. Still editable so they
    // can assign manually. The `[spk_N]` monospace label is preserved
    // so the user knows which Soniox label to work with.
    content = (
      <span className="speaker-label speaker-label-unattributed">
        <span
          className="unattributed-pill"
          title={`Claude didn't attribute speaker ${speaker}. Click to assign manually.`}
        >
          Not attributed
        </span>
        <span
          style={{
            marginLeft: 6,
            color: "var(--text-muted)",
            fontFamily: "monospace",
            fontSize: "0.85em",
          }}
        >
          [spk_{speaker}]
        </span>
      </span>
    );
  } else {
    const rawLabel = entry.raw_label ?? speaker;
    const showRaw = !isLocked || entry.name == null;

    // Graduated confidence rendering per the 2026-05-25 redesign:
    //   ≥ 0.85 → silent (no marker)
    //   0.60-0.85 → "?" glyph (medium uncertainty, today's treatment)
    //   < 0.60   → more prominent "(?) " pill + italics
    // After lock, suppress the marker for named entries (the lock IS
    // the user's acceptance). Keep it for name=null entries: the
    // uncertainty is still load-bearing even post-lock.
    let confidenceTier: "high" | "medium" | "low";
    if (entry.confidence >= CONFIDENCE_HIGH) confidenceTier = "high";
    else if (entry.confidence >= CONFIDENCE_LOW) confidenceTier = "medium";
    else confidenceTier = "low";

    const showConfMark =
      confidenceTier !== "high" && (!isLocked || entry.name == null);

    const rawSpan = (
      <span
        style={{
          marginLeft: 6,
          color: "var(--text-muted)",
          fontFamily: "monospace",
          fontSize: "0.85em",
        }}
      >
        [spk_{rawLabel}]
      </span>
    );

    let confMark: JSX.Element | null = null;
    if (showConfMark) {
      if (confidenceTier === "medium") {
        confMark = (
          <span
            className="confidence-marker"
            title={`Confidence ${(entry.confidence * 100).toFixed(0)}%`}
          >
            {" "}?
          </span>
        );
      } else {
        // low
        confMark = (
          <span
            className="confidence-marker confidence-marker-low"
            title={`Low confidence ${(entry.confidence * 100).toFixed(0)}% — please verify`}
          >
            (?)
          </span>
        );
      }
    }

    content = (
      <span className={`speaker-label confidence-${confidenceTier}`}>
        {entry.name ?? null}
        {showRaw && rawSpan}
        {confMark}
      </span>
    );
  }

  // Locked: no edit affordance, no click. Wires together with Task E; until
  // E ships, isLocked is always false and this branch never renders.
  if (isLocked) return content;

  return (
    <span
      className="speaker-label-editable"
      title="Click to rename"
      onClick={(e) => {
        e.stopPropagation();
        onEdit();
      }}
    >
      {content}
      <Icon name="edit" size={12} className="speaker-label-edit-icon" aria-hidden />
    </span>
  );
}

// Header row for the second-half of a paragraph split. Renders instead
// of the standard .who + .ts row. A dashed top border signals the
// user-created boundary. The Rejoin button removes the split entry.
//
// Click on the name or the ✎ icon enters inline-rename mode: an input
// replaces the name, Enter saves, Escape cancels, click-outside cancels.
// onRename is invoked with the new (trimmed) name.
function SplitTurnHeader({
  name,
  splitId,
  hue,
  isLocked,
  onRejoin,
  onRename,
}: {
  name: string | null;
  splitId: string;
  /** 1-8 stable per-speaker hue (Avatar + name tint); undefined = accent fallback */
  hue?: number;
  isLocked: boolean;
  onRejoin: () => void;
  onRename?: (newName: string, promote: boolean) => Promise<void> | void;
}) {
  void splitId; // used by caller for keying; not needed inside the component
  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState(name ?? "");
  const [promote, setPromote] = useState(false);
  const [savingRename, setSavingRename] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (renaming) {
      setDraftName(name ?? "");
      setPromote(false);
      // Defer focus so the input is mounted first
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [renaming, name]);

  // Click outside the popover cancels rename (unless saving).
  useEffect(() => {
    if (!renaming) return;
    function handleMouseDown(e: MouseEvent) {
      if (savingRename) return;
      if (!popoverRef.current) return;
      if (popoverRef.current.contains(e.target as Node)) return;
      setRenaming(false);
    }
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [renaming, savingRename]);

  async function commitRename() {
    if (!onRename) { setRenaming(false); return; }
    const trimmed = draftName.trim();
    if (!trimmed) { setRenaming(false); return; }
    const nameChanged = trimmed !== (name ?? "");
    const wantsPromote = promote && trimmed.length > 0;
    if (!nameChanged && !wantsPromote) { setRenaming(false); return; }
    setSavingRename(true);
    try {
      await onRename(trimmed, wantsPromote);
    } finally {
      setSavingRename(false);
      setRenaming(false);
    }
  }

  const canRename = !isLocked && !!onRename;

  return (
    <span className="split-turn-header">
      {name != null && (
        <Avatar name={name} hueIndex={hue} size={22} className="turn-avatar" />
      )}
      <span
        className={canRename ? "speaker-label-editable" : "who"}
        style={{
          color: hue !== undefined ? `var(--speaker-${hue})` : "var(--accent)",
          fontWeight: 600,
        }}
        title={canRename ? "Click to rename this speaker" : "Speaker name set when this paragraph was split"}
        onClick={(e) => {
          if (!canRename) return;
          e.stopPropagation();
          setRenaming(true);
        }}
      >
        {name ?? "Unnamed speaker"}
        {canRename && (
          <Icon name="edit" size={12} className="speaker-label-edit-icon" aria-hidden style={{ marginInlineStart: 6 }} />
        )}
      </span>
      {renaming && (
        <div
          ref={popoverRef}
          className="speaker-override"
          onClick={(e) => e.stopPropagation()}
          style={{ marginInlineStart: 12 }}
        >
          <input
            ref={inputRef}
            type="text"
            dir="auto"
            value={draftName}
            disabled={savingRename}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void commitRename();
              } else if (e.key === "Escape") {
                e.preventDefault();
                if (!savingRename) setRenaming(false);
              }
            }}
            placeholder="Speaker name"
            style={{
              width: 180,
              padding: "4px 8px",
              background: "var(--surface)",
              color: "var(--text)",
              border: "1px solid var(--border)",
              borderRadius: 4,
              fontSize: 13,
            }}
          />
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              color: "var(--text-muted)",
              margin: 0,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={promote}
              onChange={(e) => setPromote(e.target.checked)}
              disabled={savingRename}
              style={{ margin: 0 }}
            />
            Add to attendee registry
          </label>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              onClick={() => void commitRename()}
              disabled={savingRename}
              style={{ padding: "4px 10px", fontSize: 12 }}
            >
              {savingRename ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              className="secondary"
              onClick={() => !savingRename && setRenaming(false)}
              disabled={savingRename}
              style={{ padding: "4px 10px", fontSize: 12 }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {!isLocked && (
        <button
          type="button"
          className="split-rejoin-btn"
          onClick={(e) => {
            e.stopPropagation();
            onRejoin();
          }}
          title="Remove this split and merge the two paragraphs back together"
        >
          Rejoin
        </button>
      )}
    </span>
  );
}

function describeEvent(ev: MeetingEvent): string {
  const p = (ev.payload ?? {}) as Record<string, unknown>;
  switch (ev.event_type) {
    case "title_change":
      return `renamed to "${p.new_title ?? "?"}"`;
    case "speaker_label_override":
      return `renamed speaker to ${p.new_name ?? "(cleared)"}`;
    case "span_attribution_add":
      return `assigned speaker to ${p.name ?? "?"}`;
    case "span_attribution_remove":
      return "removed speaker assignment";
    case "paragraph_split":
      return "paragraph split";
    case "paragraph_rejoin":
      return "paragraph rejoin";
    case "text_correction_accept":
      return "text correction";
    case "text_correction_dismiss":
      return "undid correction";
    case "bookmark_set":
      return "bookmarked paragraph";
    case "bookmark_clear":
      return "removed bookmark";
    case "paragraph_revert": {
      const rp = p as unknown as ParagraphRevertPayload;
      const n =
        (rp.removed_corrections?.length ?? 0) +
        (rp.removed_span_attributions?.length ?? 0) +
        (rp.removed_paragraph_splits?.length ?? 0) +
        (rp.removed_bookmarks_at_ms?.length ?? 0);
      return `reset paragraph (${n} item${n === 1 ? "" : "s"} restored)`;
    }
    case "edit_session": {
      const count = Array.isArray(p.sub_event_ids) ? p.sub_event_ids.length : 0;
      return `paragraph edit (${count} change${count === 1 ? "" : "s"})`;
    }
    default:
      return ev.event_type.replace(/_/g, " ");
  }
}

// StructuredContextCard removed — context display now lives in ReviewRail's
// context disclosure section, with plain-language labels ("Your notes",
// "Details", "Names and terms") instead of the old jargon strings.
